// Bói chỉ tay — chụp ảnh bàn tay mỗi người (tối đa 5), làm nghi thức chắp tay,
// máy QUÉT ảnh theo 16 tiêu chí, người xem SOÁT BẢNG rồi nhận bài luận.
//
// Một route ba trạng thái: chưa có gì thì hiện form; sau khi quét thì hiện bảng
// 16 tiêu chí (`PalmScanStep` — sửa/bổ sung miễn phí); có `?reading=` thì tải kết
// quả từ BE (mở lại link vẫn xem được).
//
// ⚠️ HAI CỜ KHÁC NHAU, đừng gộp lại như trước:
//  · `scan_review.enabled` — luồng nháp → quét → soát bảng → luận. Đây là đường
//    đi chính, và là chỗ DUY NHẤT người xem sửa được ô máy đọc sai cũng như khai
//    vân đầu ngón / móng SAU KHI đã thấy máy đọc ra gì.
//  · `segmentation.enabled` — model vẽ nét đường chỉ tay, hiện đang TẮT ở BE. Nó
//    chỉ là một cách bổ sung mở ra từ bảng (`onDraw`), không phải một bước.
// Gộp hai cờ là tắt model vẽ nét thì mất luôn màn soát bảng — người xem quay về
// một cú `/palm/start` không sửa được gì.
// `/palm/start` vẫn giữ làm đường lùi cho BE cũ chưa có luồng nháp.
import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Check,
  CircleCheckBig,
  ChevronLeft,
  ChevronRight,
  ImagePlus,
  Loader2,
  Pencil,
  Plus,
  Settings2,
  Sparkles,
  Trash2,
  UserRound,
  X,
} from "lucide-react";
import { toast } from "sonner";

import {
  BirthInputForm,
  birthValueFromProfile,
  defaultBirthValue,
  isBirthHourSet,
  isBirthValueComplete,
  type BirthFormValue,
} from "@/components/boi-de/birth-input-form";
import { formatVnd } from "@/components/boi-de/topic-style";
import { DivinerPicker } from "@/components/diviner-picker";
import {
  GuidedHandCapture,
  hasAllPalmImages,
  reindexPalmImages,
  type PalmImageMap,
} from "@/components/palm/hand-capture";
import { HandChoice } from "@/components/palm/hand-choice";
import { FingerprintSelector } from "@/components/palm/fingerprint-selector";
import { NailSelector } from "@/components/palm/nail-selector";
import { PalmConsentGate, usePalmConsent } from "@/components/palm/palm-consent-gate";
import { PalmReadingView } from "@/components/palm/palm-reading-view";
import { usePalmProfiles } from "@/components/palm/palm-saved-hands";
import { PalmReviewStep } from "@/components/palm/palm-review-step";
import { PalmScanStep } from "@/components/palm/palm-scan-step";
import {
  ThumbRitual,
  type DominantHandChoice,
  type ThumbChoice,
} from "@/components/palm/thumb-ritual";
import { PalmTopicPicker } from "@/components/palm/topic-picker";
import { IconChiTay, IconPalmArchive } from "@/components/nav-icons";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { displayName, refreshBoideUser, useBoideUser } from "@/hooks/use-boide-user";
import {
  createPalmDraft,
  deletePalmDraft,
  deletePalmProfile,
  deletePalmReading,
  getPalmConfig,
  getPalmProfileImage,
  getPalmReading,
  isPalmJob,
  listPalmReadings,
  isPalmDraftItem,
  palmStageLabel,
  PALM_READINGS_PAGE_SIZE,
  listPalmTopics,
  PALM_SLOTS,
  palmImageField,
  getPalmDraft,
  listPalmDrafts,
  scanPalmDraft,
  startPalmReading,
  type PalmDraft,
  type PalmHand,
  type PalmPersonInput,
  type PalmProfile,
  type PalmFingerprintSelection,
  type PalmNailSelection,
  type PalmReading,
  type PalmScanResult,
  type PalmReadingListItem,
  type PalmConfig,
  type PalmSlot,
  type PalmTopic,
} from "@/lib/boide-api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { rememberPalmJob } from "@/lib/palm-job-watch";

export const Route = createFileRoute("/_authenticated/boi-chi-tay")({
  validateSearch: (search: Record<string, unknown>): { reading?: string; draft?: string } => ({
    reading: typeof search.reading === "string" ? search.reading : undefined,
    // `?draft=` là đích của push "đã đọc xong bàn tay" (v92). Không khai ở đây thì
    // TanStack Router bỏ tham số trong im lặng và người bấm thông báo rơi về trang
    // trắng, phải tự tìm lại lượt của mình trong danh sách nháp.
    draft: typeof search.draft === "string" ? search.draft : undefined,
  }),
  component: PalmPage,
});

/**
 * Vùng cuộn của route.
 *
 * App shell chỉ cho mỗi route một khung `h-dvh` đã `overflow-hidden`, nên TRANG
 * phải tự dựng `main` cuộn — thiếu nó thì nội dung dài bị cắt cụt và không cuộn
 * được. Cùng khuôn với `_authenticated.gioi-thieu.tsx`, `index.tsx`…
 */
function Shell({
  children,
  className = "",
  scrollToTopKey,
}: {
  children: React.ReactNode;
  className?: string;
  /** Đổi key khi vào một nội dung mới cần bắt đầu từ đầu vùng cuộn. */
  scrollToTopKey?: string;
}) {
  const scrollerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!scrollToTopKey) return;
    scrollerRef.current?.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [scrollToTopKey]);

  return (
    <main
      ref={scrollerRef}
      className="app-feature-page app-feature-bg app-feature-bg--chi-tay flex-1 overflow-y-auto px-4 py-6 pb-[calc(6rem+env(safe-area-inset-bottom))] md:px-8 md:py-10 md:pb-10"
    >
      <div className={`mx-auto w-full max-w-2xl ${className}`}>{children}</div>
    </main>
  );
}

const LOADING_STEPS = [
  "Đang soi từng tấm ảnh bàn tay…",
  "Dò 9 đường chỉ tay, 10 gò và các dấu hiệu đặc biệt…",
  "Lập lá số Tử Vi và Bát Tự để soi chiếu…",
  "Viết lời luận và ghép bài hoàn chỉnh…",
];

function PalmLoading() {
  const [step, setStep] = useState(0);
  useEffect(() => {
    setStep(0);
    const t = setInterval(() => setStep((s) => Math.min(s + 1, LOADING_STEPS.length - 1)), 12000);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="flex flex-col items-center gap-4 rounded-2xl border border-gold-soft/45 bg-card/55 px-6 py-14 text-center backdrop-blur">
      <span className="relative flex size-16 items-center justify-center">
        <span className="absolute inset-0 animate-ping rounded-full bg-primary/15" />
        <IconChiTay className="size-8 text-primary" />
      </span>
      <p className="font-display text-base text-primary">Thầy đang luận tướng tay</p>
      <p className="max-w-xs text-[12px] leading-5 text-muted-foreground">{LOADING_STEPS[step]}</p>
      <div className="flex gap-1.5">
        {LOADING_STEPS.map((_, i) => (
          <span
            key={i}
            className={`h-1 w-6 rounded-full transition ${i <= step ? "bg-primary/70" : "bg-muted"}`}
          />
        ))}
      </div>
      <p className="text-[11px] text-muted-foreground/80">
        Đọc nhiều ảnh và viết bài dài nên mất khoảng 1–3 phút, đừng rời trang nhé.
      </p>
    </div>
  );
}

type PersonForm = {
  birth: BirthFormValue;
  thumb: ThumbChoice;
  fingerprints: PalmFingerprintSelection;
  nail: PalmNailSelection;
  /** Tay thuận (`thuan_tay`) — khác với `thumb` (ngón cái nào nằm dưới khi chắp tay). */
  dominantHand: DominantHandChoice;
  /**
   * Chỉ xem tay — không khai thông tin sinh, lượt này không có lá số Tử Vi/Bát Tự.
   *
   * Cần một cờ riêng chứ không dựa vào "ô ngày sinh còn trống": `BirthInputForm`
   * dùng chung với Bói Đê nên nó luôn khởi tạo sẵn 01/01/2000, tức là form chưa ai
   * chạm tới vẫn gửi lên một ngày sinh trông như thật.
   */
  skipBirth: boolean;
  /**
   * Ngày sinh đã có người thật sự khai (gõ tay, hoặc lấy từ hồ sơ) hay chưa.
   *
   * Cùng lý do với `skipBirth`: form khởi tạo sẵn 01/01/2000, nên một người bật
   * "thêm đối chiếu Tử Vi – Bát Tự" rồi bấm luận ngay sẽ gửi lên một ngày sinh
   * trông như thật mà chưa ai khai. Lá số dựng trên đó sai từ gốc, và không có gì
   * trong bài luận cho thấy điều đó.
   */
  birthTouched: boolean;
  /**
   * Bàn tay ĐÃ LƯU dùng cho người này — khung nào hồ sơ đã có thì khỏi chụp lại.
   * `null` = người này chụp mới hoàn toàn.
   */
  profileId: string | null;
  /**
   * Tay người này chọn để chụp, khi thẻ chỉ cần MỘT tay (`PalmTopic.hand_choice`).
   * Khác `thumb` (ngón cái nào nằm dưới khi chắp tay) — đây là "chụp tay nào".
   */
  hand: PalmHand | null;
  /**
   * Ô này do THẺ tự mở ra (`min_persons`), không phải người xem bấm "Xem thêm người".
   *
   * Nguồn gốc, không phải "đã nhập gì chưa" — và đó là điểm mấu chốt. Thẻ nhiều
   * người CHẶN nút "Bắt đầu chụp ảnh" cho tới khi mọi ô có họ tên, nên chỉ cần đi
   * tiếp một bước là ô nào cũng "đã nhập" hết; lấy dữ liệu ra làm căn cứ thì không
   * bao giờ đóng lại được ô mà thẻ mới không cần nữa.
   */
  autoAdded: boolean;
};

function emptyPerson(autoAdded: boolean): PersonForm {
  return {
    birth: defaultBirthValue(),
    thumb: null,
    fingerprints: {},
    nail: {},
    dominantHand: null,
    skipBirth: false,
    birthTouched: false,
    profileId: null,
    hand: null,
    autoAdded,
  };
}

/** Ô người này có gì đáng tiếc khi mất không — dùng để biết có cần báo hay không. */
function personHasInput(person: PersonForm, index: number, images: PalmImageMap): boolean {
  if (person.birthTouched || person.skipBirth || person.profileId) return true;
  if (person.thumb || person.dominantHand || person.hand) return true;
  if (Object.keys(person.fingerprints).length > 0) return true;
  if (Object.values(person.nail).some(Boolean)) return true;
  return PALM_SLOTS.some((slot) => images.has(palmImageField(index, slot)));
}

/** Ngày ngắn, ổn định giữa SSR/client để phân biệt các hồ sơ trùng tên. */
function savedProfileMeta(profile: PalmProfile): string {
  const rawDate = profile.last_used_at ?? profile.created_at ?? "";
  const match = rawDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return `${profile.images.length} ảnh${match ? ` · ${match[3]}/${match[2]}` : ""}`;
}

/**
 * Chọn một BÀN TAY ĐÃ LƯU cho người đang nhập.
 *
 * Chỉ mời hồ sơ MỘT người: hồ sơ nhiều người thì "lấy bàn tay của ai" là câu hỏi
 * không ai trả lời được, và đoán hộ là đoán sai bàn tay của một người thật. Hồ sơ
 * nhiều người vẫn dùng được ở luồng "Hỏi chủ đề khác trên bàn tay đã lưu".
 */
function PalmSavedHandPicker({
  value,
  personName,
  onPick,
  embedded = false,
}: {
  value: string | null;
  personName: string;
  onPick: (profile: PalmProfile | null) => void;
  embedded?: boolean;
}) {
  const queryClient = useQueryClient();
  const profiles = usePalmProfiles();
  /** Hồ sơ đang chờ xác nhận xoá — xoá là mất ảnh thật, không hỏi lại là không được. */
  const [confirming, setConfirming] = useState<string | null>(null);
  const remove = useMutation({
    mutationFn: deletePalmProfile,
    onSuccess: (_data, profileId) => {
      setConfirming(null);
      void queryClient.invalidateQueries({ queryKey: ["palm-profiles"] });
      // Đang chọn đúng hồ sơ vừa xoá thì phải kéo về "Chụp mới": để nguyên là form
      // trỏ tới một hồ sơ không còn tồn tại, và lỗi chỉ nổ lúc bấm bói.
      if (profileId === value) onPick(null);
      toast.success("Đã xoá bàn tay đã lưu cùng ảnh của nó");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Không xoá được"),
  });

  const items = (profiles.data?.profiles ?? []).filter((p) => p.persons_count === 1);
  if (!items.length) return null;

  const picked = items.find((p) => p.profile_id === value) ?? null;

  const content = (
    <>
      <div>
        <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-primary/85">
          Chọn nguồn ảnh
        </p>
        <p className="mt-0.5 text-[11px] leading-4 text-muted-foreground">
          Chụp mới cho {personName}, hoặc dùng lại ảnh đã lưu và chỉ bổ sung phần còn thiếu.
        </p>
      </div>
      <div
        className="grid grid-cols-2 gap-2 min-[520px]:grid-cols-3"
        role="radiogroup"
        aria-label={`Nguồn ảnh bàn tay của ${personName}`}
      >
        <button
          type="button"
          onClick={() => onPick(null)}
          role="radio"
          aria-checked={!picked}
          aria-pressed={!picked}
          className={`flex min-h-[52px] items-center gap-2 rounded-xl border px-3 text-left transition ${
            !picked
              ? "border-primary/65 bg-primary/10 text-foreground shadow-[0_3px_10px_rgba(123,82,18,0.07)]"
              : "border-border/45 bg-background/40 text-muted-foreground hover:border-primary/40"
          }`}
        >
          <span
            className={`grid size-4 shrink-0 place-items-center rounded-full border ${
              !picked ? "border-primary" : "border-border/70"
            }`}
            aria-hidden="true"
          >
            {!picked ? <span className="size-2 rounded-full bg-primary" /> : null}
          </span>
          <span>
            <span className="block text-[11.5px] font-semibold">Chụp mới</span>
            <span className="mt-0.5 block text-[9.5px] font-normal text-muted-foreground">
              Dùng camera
            </span>
          </span>
        </button>
        {items.map((profile) => {
          const active = profile.profile_id === value;
          const asking = confirming === profile.profile_id;

          // Bước hỏi lại chiếm trọn ô: thẻ chỉ cao 52px trong lưới 3 cột, nhét thêm hai
          // nút vào cạnh tên là vỡ layout trên điện thoại.
          if (asking) {
            return (
              <div
                key={profile.profile_id}
                className="flex min-h-[52px] flex-col justify-center gap-1 rounded-xl border border-destructive/50 bg-destructive/5 px-3 py-2"
              >
                <p className="truncate text-[10.5px] font-semibold text-foreground">
                  Xoá “{profile.name}”? Mất luôn ảnh đã lưu.
                </p>
                <div className="flex items-center gap-1.5">
                  <Button
                    size="sm"
                    variant="destructive"
                    className="h-7 px-2 text-[10.5px]"
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(profile.profile_id)}
                  >
                    {remove.isPending ? <Loader2 className="size-3 animate-spin" /> : "Xoá hẳn"}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 text-[10.5px]"
                    onClick={() => setConfirming(null)}
                  >
                    Thôi
                  </Button>
                </div>
              </div>
            );
          }

          return (
            // ⚠️ Nút xoá phải nằm NGOÀI nút chọn: <button> lồng trong <button> là HTML
            // không hợp lệ, trình duyệt tự gỡ và cái bên trong mất luôn sự kiện click.
            <div key={profile.profile_id} className="relative">
              <button
                type="button"
                onClick={() => onPick(profile)}
                role="radio"
                aria-checked={active}
                aria-pressed={active}
                className={`flex min-h-[52px] w-full items-center gap-2 rounded-xl border py-1.5 pl-3 pr-9 text-left transition ${
                  active
                    ? "border-primary/65 bg-primary/10 text-foreground shadow-[0_3px_10px_rgba(123,82,18,0.07)]"
                    : "border-border/45 bg-background/40 text-muted-foreground hover:border-primary/40"
                }`}
              >
                <span
                  className={`grid size-4 shrink-0 place-items-center rounded-full border ${
                    active ? "border-primary" : "border-border/70"
                  }`}
                  aria-hidden="true"
                >
                  {active ? <span className="size-2 rounded-full bg-primary" /> : null}
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-[11.5px] font-semibold">{profile.name}</span>
                  <span className="mt-0.5 block text-[9.5px] font-normal text-muted-foreground/80">
                    Đã lưu · {savedProfileMeta(profile)}
                  </span>
                </span>
              </button>
              <button
                type="button"
                aria-label={`Xoá bàn tay đã lưu ${profile.name}`}
                onClick={() => setConfirming(profile.profile_id)}
                className="absolute right-1 top-1/2 grid size-7 -translate-y-1/2 place-items-center rounded-lg text-muted-foreground/70 transition hover:bg-destructive/10 hover:text-destructive"
              >
                <Trash2 className="size-3.5" />
              </button>
            </div>
          );
        })}
      </div>
    </>
  );

  if (embedded) {
    return <div className="space-y-2.5">{content}</div>;
  }

  return (
    <section className="space-y-2 rounded-2xl border border-gold-soft/45 bg-card/55 p-3.5">
      {content}
    </section>
  );
}

/** Tay MẶC ĐỊNH của thẻ — suy từ khung ảnh tối thiểu (`slots[0]`), vd `right_palm` → phải. */
function defaultHandOf(topic: PalmTopic | null): PalmHand {
  return topic?.slots?.[0]?.startsWith("left") ? "left" : "right";
}

/** Khung ảnh TỐI THIỂU của một người, sau khi áp lựa chọn tay của chính họ. */
function requiredSlotsOf(topic: PalmTopic | null, hand: PalmHand | null): PalmSlot[] | undefined {
  if (!topic?.hand_choice) return topic?.slots;
  const chosen = hand ?? defaultHandOf(topic);
  return topic.slot_labels_by_hand?.[chosen]?.map((s) => s.key) ?? topic?.slots;
}

/** Khung ảnh còn lại (ngoài mức tối thiểu) mà người xem CÓ THỂ chụp thêm — có phí. */
function optionalSlotsOf(topic: PalmTopic | null, required: PalmSlot[] | undefined): PalmSlot[] {
  const wanted = required?.length ? required : (topic?.slots ?? []);
  if (!wanted.length) return []; // đủ 4 khung rồi = không còn gì để thêm
  return PALM_SLOTS.filter((s) => !wanted.includes(s));
}

/** Ngày giờ VN gọn cho danh sách đã lưu. */
function shortVnDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("vi-VN", {
    hour: "2-digit",
    minute: "2-digit",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/**
 * Những lượt xem tay đã lưu — mở lại miễn phí.
 *
 * Bài luận đã trả tiền nằm sẵn trong DB nhưng trước đây chỉ vào lại được nếu
 * người dùng còn giữ link `?reading=`; mất link là coi như mất bài.
 */
function SavedReadings({ onOpen }: { onOpen: (id: string, isDraft: boolean) => void }) {
  const queryClient = useQueryClient();
  const { confirm, confirmDialog } = useConfirm();
  const [page, setPage] = useState(1);
  /**
   * Mỗi bài mang cả `input_data` (persons + snapshot thẻ) nên tải cả danh sách là đọc
   * thừa gần hết. `placeholderData: keepPreviousData` giữ trang cũ trên màn hình trong
   * lúc trang mới đang bay — không có nó thì mỗi lần bấm sang trang, danh sách trống
   * một nhịp và hộp thoại nhảy chiều cao.
   */
  const list = useQuery({
    queryKey: ["palm-readings", page],
    queryFn: () => listPalmReadings(page),
    placeholderData: keepPreviousData,
  });
  const [busyId, setBusyId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const items = list.data?.items ?? [];
  const total = list.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PALM_READINGS_PAGE_SIZE));

  const remove = useMutation({
    mutationFn: (id: string) => deletePalmReading(id),
    onMutate: (id: string) => setBusyId(id),
    onSuccess: () => {
      toast.success("Đã xoá lượt xem");
      // Xoá một bài là DỜI mọi bài sau nó sang trang khác, nên phải bỏ cache CẢ tiền
      // tố `["palm-readings"]` — refetch riêng trang đang xem để lại các trang khác
      // với dữ liệu đã lệch.
      void queryClient.invalidateQueries({ queryKey: ["palm-readings"] });
      // Bài luận và phiên đàm đạo của nó là HAI bản ghi ở hai danh sách khác nhau
      // (trang này và trang Lịch sử). Máy chủ nay xoá cả hai, nhưng cache của trang
      // Lịch sử thì không tự biết — thiếu dòng này là người xem quay sang Lịch sử
      // vẫn thấy y nguyên dòng vừa xoá, tưởng máy chủ chưa xoá.
      void queryClient.invalidateQueries({ queryKey: ["boide-sessions"] });
      // Xoá nốt bài cuối của trang cuối thì trang ấy không còn tồn tại; đứng lại đó là
      // nhìn một danh sách rỗng dù vẫn còn bài ở trang trước.
      if (items.length === 1 && page > 1) setPage((p) => p - 1);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Không xoá được"),
    onSettled: () => setBusyId(null),
  });

  if (list.isLoading) return <Skeleton className="h-24 w-full rounded-2xl" />;
  // Xét `total`, KHÔNG xét `items.length`: trang rỗng (vừa xoá hết bài trên đó) không
  // phải là "chưa có bài nào" — ẩn cả khối lúc ấy là làm mất đường vào lịch sử.
  if (!total) return null;

  return (
    <section className="rounded-2xl border border-gold-soft/35 bg-card/35 backdrop-blur">
      <button
        type="button"
        onClick={() => {
          // Mở lại là về trang 1: bài mới nhất luôn ở đó, và `page` là state sống lâu
          // hơn hộp thoại nên không reset là lần sau mở ra đứng giữa danh sách.
          setPage(1);
          setOpen(true);
        }}
        className="flex min-h-14 w-full items-center gap-3 px-3.5 py-2.5 text-left transition hover:bg-primary/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset"
      >
        <span className="flex size-9 shrink-0 items-center justify-center rounded-xl border border-primary/20 bg-primary/10 text-primary">
          <IconPalmArchive className="size-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-gold-gradient">Lịch sử luận giải</span>
          <span className="mt-0.5 block text-[10px] text-muted-foreground">
            Chạm để mở lại các bài luận trước
          </span>
        </span>
        {/* TỔNG số bài, không phải số bài của trang đang xem — badge là thứ người xem
            đọc để biết mình có bao nhiêu bài, hiện "10" cho người có 34 bài là nói sai. */}
        <span className="rounded-full bg-muted/60 px-2 py-0.5 text-[10px] text-muted-foreground">
          {total}
        </span>
        <ChevronRight className="size-4 shrink-0 text-primary" aria-hidden="true" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        {/* `flex flex-col` là BẮT BUỘC, không phải trang trí: `DialogContent` mặc định
            là `grid`, và hàng grid ngầm có `height: auto` nên danh sách cứ dài ra rồi
            bị `overflow-hidden` của hộp thoại CẮT — `overflow-y-auto` dưới kia không có
            chiều cao nào để cuộn trong đó. Lịch sử vượt vài bài là không kéo xuống được.
            Cùng khuôn với hộp thoại "Tăng độ chi tiết" bên dưới: content `flex-col` ·
            header `shrink-0` · vùng cuộn `min-h-0 flex-1`. */}
        <DialogContent className="flex max-h-[min(680px,calc(100dvh-32px))] w-[calc(100%-24px)] flex-col gap-0 overflow-hidden rounded-[26px] border-gold-soft/55 bg-background p-0 sm:max-w-lg">
          <DialogHeader className="shrink-0 border-b border-border/35 px-4 py-4 text-left sm:px-5">
            <DialogTitle className="font-display text-lg text-foreground">
              Lịch sử luận giải
            </DialogTitle>
            <DialogDescription className="mt-1 text-xs">
              Mở lại bài luận cũ, hoặc đi tiếp một lượt còn dở — đều không mất thêm tiền.
            </DialogDescription>
          </DialogHeader>
          <ul className="min-h-0 flex-1 space-y-1.5 overflow-y-auto px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:px-5">
            {items.map((item: PalmReadingListItem) => {
              const names = item.names.filter(Boolean).join(" & ") || "Không rõ tên";
              // Lượt còn dở mở bằng `?draft=` (đi tiếp bước quét/luận), bài đã xong
              // mở bằng `?reading=`. Chọn nhầm nhánh là đưa người xem tới một trang
              // trống: id nháp không có bài luận nào để tải.
              const isDraft = isPalmDraftItem(item);
              const stageLabel = palmStageLabel(item);
              return (
                <li key={item.id} className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      onOpen(item.id, isDraft);
                    }}
                    className={`min-h-14 min-w-0 flex-1 rounded-xl border bg-card/40 px-3 py-2 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                      isDraft
                        ? "border-amber-500/45 hover:border-amber-500/70"
                        : "border-border/45 hover:border-primary/50"
                    }`}
                  >
                    <p className="truncate text-[12px] font-medium text-foreground">{names}</p>
                    <p className="truncate text-[10px] text-muted-foreground">
                      {shortVnDate(item.created_at)}
                      {item.persons_count > 1 ? ` · ${item.persons_count} người` : ""}
                      {item.question ? ` · ${item.question}` : ""}
                    </p>
                    {/* Lượt này đã bị trừ tiền rồi — nói thẳng nó đang ở đâu, đừng để
                        nó nằm lẫn giữa các bài đã xong mà không có dấu hiệu gì. */}
                    {stageLabel ? (
                      <p className="mt-1 truncate text-[10px] font-medium text-amber-500">
                        {stageLabel}
                      </p>
                    ) : null}
                  </button>
                  <button
                    type="button"
                    aria-label={`Xoá lượt xem ${names}`}
                    disabled={busyId === item.id}
                    onClick={async () => {
                      // Bài luận đã TRẢ TIỀN và xoá đi là mất luôn ảnh bàn tay lẫn
                      // phiên hỏi đáp — nói đúng cái sắp mất, đừng chỉ nói "không
                      // thể hoàn tác".
                      const ok = await confirm({
                        title: "Xoá lượt xem tướng tay?",
                        description: isDraft ? (
                          <>
                            Lượt “{names}” chưa luận xong nhưng ĐÃ bị trừ tiền. Xoá là mất ảnh bàn
                            tay và bảng dấu hiệu đã quét, không thể hoàn tác và không hoàn tiền —
                            bấm vào nó để đi tiếp thì không mất thêm đồng nào.
                          </>
                        ) : (
                          <>
                            Bài luận “{names}”, ảnh bàn tay đã gửi và phần hỏi đáp kèm theo sẽ bị
                            xoá, ở cả trang Lịch sử. Hành động này không thể hoàn tác và không hoàn
                            tiền.
                          </>
                        ),
                        confirmLabel: "Xoá lượt xem",
                      });
                      if (ok) remove.mutate(item.id);
                    }}
                    className="flex size-11 shrink-0 items-center justify-center rounded-xl border border-border/45 text-muted-foreground transition hover:border-red-500/50 hover:text-red-400 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    {busyId === item.id ? (
                      <Loader2 className="size-3.5 animate-spin" />
                    ) : (
                      <Trash2 className="size-3.5" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
          {/* Chỉ hiện khi thật sự có nhiều trang — một thanh điều hướng chết nằm dưới
              danh sách 3 bài chỉ làm hộp thoại chật hơn. `shrink-0` để nó không bị vùng
              cuộn ở trên ăn mất chỗ. */}
          {pageCount > 1 ? (
            <div className="flex shrink-0 items-center justify-between gap-2 border-t border-border/35 px-4 py-2.5 sm:px-5">
              <button
                type="button"
                aria-label="Trang trước"
                disabled={page <= 1 || list.isFetching}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="flex size-9 items-center justify-center rounded-xl border border-border/45 text-muted-foreground transition hover:border-primary/50 hover:text-primary disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <ChevronLeft className="size-4" />
              </button>
              <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                {list.isFetching ? <Loader2 className="size-3 animate-spin" /> : null}
                Trang {page}/{pageCount}
              </span>
              <button
                type="button"
                aria-label="Trang sau"
                disabled={page >= pageCount || list.isFetching}
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                className="flex size-9 items-center justify-center rounded-xl border border-border/45 text-muted-foreground transition hover:border-primary/50 hover:text-primary disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <ChevronRight className="size-4" />
              </button>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
      {confirmDialog}
    </section>
  );
}

type PalmInputStep = "profile" | "capture" | "finish";

const INPUT_STEPS: { key: PalmInputStep; label: string }[] = [
  { key: "profile", label: "Thông tin" },
  { key: "capture", label: "Chụp ảnh bàn tay" },
  { key: "finish", label: "Hoàn tất" },
];

function formatBirthSummary(value: BirthFormValue): string {
  const date = `${String(value.ngay).padStart(2, "0")}/${String(value.thang).padStart(2, "0")}/${value.nam}`;
  const hour = value.gio < 0 ? "không rõ giờ" : `${value.gio}h`;
  const calendar = value.is_lunar
    ? value.is_leap_month
      ? "Âm lịch (tháng nhuận)"
      : "Âm lịch"
    : "Dương lịch";
  return `${date} · ${hour} · ${calendar}`;
}

function InputStepper({
  active,
  canCapture,
  canFinish,
  onChange,
}: {
  active: PalmInputStep;
  canCapture: boolean;
  canFinish: boolean;
  onChange: (step: PalmInputStep) => void;
}) {
  return (
    <nav
      aria-label="Các bước xem chỉ tay"
      className="rounded-2xl border border-gold-soft/45 bg-card/55 p-1.5 shadow-[0_8px_24px_rgba(77,48,19,0.06)]"
    >
      <ol className="flex gap-1 sm:grid sm:grid-cols-3">
        {INPUT_STEPS.map((item, index) => {
          const isActive = active === item.key;
          const allowed = index === 0 || (index === 1 ? canCapture : canFinish);
          return (
            <li
              key={item.key}
              className={isActive ? "flex-1 min-w-0 sm:w-full" : "flex-initial sm:w-full"}
            >
              <button
                type="button"
                disabled={!allowed}
                onClick={() => onChange(item.key)}
                aria-current={isActive ? "step" : undefined}
                className={`flex min-h-11 w-full items-center justify-center gap-1.5 rounded-xl px-2 text-[11px] font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary md:text-xs ${
                  isActive
                    ? "bg-gold-gradient text-primary-foreground shadow-gold"
                    : allowed
                      ? "text-muted-foreground hover:bg-primary/[0.06] hover:text-foreground"
                      : "cursor-not-allowed text-muted-foreground/40"
                }`}
              >
                <span
                  className={`grid size-5 shrink-0 place-items-center rounded-full border text-[10px] ${isActive ? "border-white/35" : "border-current/20"}`}
                >
                  {index + 1}
                </span>
                <span className={isActive ? "inline" : "hidden sm:inline"}>{item.label}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function ProfileSummary({
  value,
  onEdit,
  onRemove,
  hideBirth = false,
  embedded = false,
}: {
  value: BirthFormValue;
  onEdit: () => void;
  /**
   * Bỏ người này ra khỏi lượt xem. Có mặt = người thứ hai trở đi.
   *
   * ⚠️ Phải có ở ĐÂY chứ không chỉ ở `BirthInputForm`: điền xong tên là thẻ này
   * thay chỗ form, và khi ấy đường bỏ người vừa thêm biến mất — người xem chỉ còn
   * cách xoá tên đi cho form hiện lại, mà chẳng ai đoán ra thao tác đó.
   */
  onRemove?: () => void;
  /**
   * Lượt này chưa hỏi ngày sinh (hỏi sau, khi bàn tay đọc xong) → KHÔNG in dòng
   * ngày sinh. Form khởi tạo sẵn 01/01/2000, mà in nó ra ở đây thì người xem tưởng
   * con số ấy đang được dùng để lập lá số.
   */
  hideBirth?: boolean;
  embedded?: boolean;
}) {
  const content = (
    <>
      <div className="flex items-start gap-3">
        <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
          <UserRound className="size-5" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary/80">
            Hồ sơ đang xem
          </p>
          <h2 className="mt-1 truncate font-display text-lg font-semibold text-foreground">
            {value.ho_ten.trim()}
          </h2>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">
            {value.gioi_tinh === "nu" ? "Nữ" : "Nam"}
            {hideBirth ? " · chưa khai ngày sinh" : ` · ${formatBirthSummary(value)}`}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <button
            type="button"
            onClick={onEdit}
            className="inline-flex min-h-11 shrink-0 items-center gap-1.5 rounded-xl border border-border/45 px-3 text-xs font-semibold text-muted-foreground transition hover:border-primary/50 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <Pencil className="size-3.5" aria-hidden="true" /> Sửa
          </button>
          {onRemove ? (
            <button
              type="button"
              onClick={onRemove}
              aria-label={`Bỏ ${value.ho_ten.trim() || "người này"} khỏi lượt xem`}
              className="inline-flex size-11 shrink-0 items-center justify-center rounded-xl border border-border/45 text-muted-foreground transition hover:border-red-400/60 hover:text-red-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <X className="size-4" aria-hidden="true" />
            </button>
          ) : null}
        </div>
      </div>
      <div className="mt-4 grid gap-2 border-t border-border/30 pt-3 text-xs text-muted-foreground sm:grid-cols-2">
        <p>
          {hideBirth
            ? "✓ Ngày sinh hỏi sau, khi bàn tay đã đọc xong"
            : "✓ Dùng thông tin này ghép cùng lá số"}
        </p>
        <p>✓ Bạn có thể sửa lại trước khi chụp</p>
      </div>
    </>
  );

  if (embedded) {
    return <div className="space-y-4">{content}</div>;
  }

  return (
    <section className="rounded-2xl border border-gold-soft/45 bg-card/55 p-4 shadow-[0_12px_30px_rgba(77,48,19,0.05)] backdrop-blur">
      {content}
    </section>
  );
}

/**
 * Tuỳ chọn nâng cao TRƯỚC KHI QUÉT: tay chính (chắp tay) · vân đầu ngón · móng tay.
 *
 * Chỉ những thứ tầng quét cần biết trước khi soi ảnh. Câu hỏi, thông tin sinh và
 * gieo quẻ đã chuyển xuống bước sau bảng — bàn tay phải được xử lý xong đã.
 *
 * ⚠️ Vân và móng khai được ở HAI chỗ, và đó là cố ý: ở đây (trước khi chụp) cho
 * người đã biết mình muốn khai, và ở bước soát bảng (sau khi máy quét) cho người
 * muốn xem máy đọc ra gì rồi mới sửa. Cùng ghi vào một field — `van_dau_ngon` /
 * `dac_diem_mong` của từng người — nên khai ở đây thì bước sau mở ra đã thấy điền
 * sẵn (BE trả lại trong `scan.persons[].van_dau_ngon`), không phải khai hai lần.
 * Bỏ trống cả hai chỗ thì bài luận dùng phần máy đọc được từ ảnh.
 */
function PalmAdvancedOptionsDialog({
  open,
  onOpenChange,
  person,
  config,
  onChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  person: PersonForm;
  config?: PalmConfig;
  onChange: (next: PersonForm) => void;
}) {
  const fingerprintCount = Object.values(person.fingerprints).reduce(
    (sum, hand) => sum + Object.keys(hand ?? {}).length,
    0,
  );
  const nailCount = [person.nail.shape, person.nail.surface, person.nail.ridge].filter(
    Boolean,
  ).length;

  return (
    <Dialog modal={true} open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideClose
        className="inset-0 left-0 top-0 flex h-dvh max-h-dvh w-full max-w-none translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden rounded-none border-0 bg-background p-0 sm:inset-1/2 sm:h-[min(92dvh,900px)] sm:w-[min(680px,calc(100%-32px))] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-[28px] sm:border sm:border-gold-soft/50"
      >
        <DialogHeader className="shrink-0 border-b border-border/35 px-4 py-4 text-left sm:px-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              aria-label="Đóng tùy chọn nâng cao"
              className="grid size-11 shrink-0 place-items-center rounded-xl border border-border/45 text-muted-foreground transition hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <ChevronLeft className="size-5" />
            </button>
            <div className="min-w-0 flex-1">
              <DialogTitle className="font-display text-lg font-semibold text-foreground">
                Tăng độ chi tiết
              </DialogTitle>
              <DialogDescription className="mt-1 text-xs leading-5">
                Không bắt buộc · các lựa chọn giúp thầy soi kỹ hơn
              </DialogDescription>
            </div>
            <Settings2 className="size-5 text-primary" aria-hidden="true" />
          </div>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4 pb-[calc(2rem+env(safe-area-inset-bottom))] sm:px-6">
          {/* Tay chính (Nghi thức chắp tay) */}
          <ThumbRitual
            value={person.thumb}
            onChange={(thumb) => onChange({ ...person, thumb, hand: thumb })}
            instruction={config?.ritual_instruction}
          />

          {/* Vân đầu ngón — thứ ảnh gần như không bao giờ đọc nổi, nên khai được là
              lãi nhất trong cả dialog này. Khai ở đây hay ở bước soát bảng đều được,
              cùng ghi vào một field (xem docstring của dialog). */}
          <section className="space-y-2 rounded-2xl border border-gold-soft/45 bg-card/55 p-3.5">
            <div className="flex items-baseline justify-between gap-2">
              <p className="text-sm font-semibold text-foreground">Vân đầu ngón</p>
              <span className="shrink-0 text-[11px] text-muted-foreground">
                {fingerprintCount}/10 ngón
              </span>
            </div>
            <p className="text-[11px] leading-4 text-muted-foreground">
              Bạn tự soi mười đầu ngón rồi chọn — ảnh chụp hiếm khi đủ nét để máy đọc phần này. Bỏ
              trống cũng được, và bạn vẫn khai lại được sau khi máy quét xong.
            </p>
            <FingerprintSelector
              value={person.fingerprints}
              config={config?.fingerprints}
              onChange={(fingerprints) => onChange({ ...person, fingerprints })}
            />
          </section>

          <section className="space-y-2 rounded-2xl border border-gold-soft/45 bg-card/55 p-3.5">
            <div className="flex items-baseline justify-between gap-2">
              <p className="text-sm font-semibold text-foreground">Móng tay</p>
              <span className="shrink-0 text-[11px] text-muted-foreground">{nailCount}/3</span>
            </div>
            <p className="text-[11px] leading-4 text-muted-foreground">
              Máy đọc móng từ ảnh mu bàn tay; bạn khai thì lấy theo bạn, bỏ trống thì lấy theo máy.
            </p>
            <NailSelector
              value={person.nail}
              config={config?.nails}
              onChange={(nail) => onChange({ ...person, nail })}
            />
          </section>

          {/* Gieo quẻ KHÔNG còn ở đây: nó gieo theo câu hỏi, mà câu hỏi nay được
              hỏi sau khi bàn tay đã đọc xong. Dialog này chỉ giữ những thứ tầng quét
              cần biết trước — chắp tay, vân, móng. */}
          <p className="rounded-xl border border-border/35 bg-card/40 px-3 py-2.5 text-[11px] leading-4 text-muted-foreground">
            Bỏ qua hết cũng xem được: thầy dùng phần máy đọc từ chính ảnh bạn gửi. Sau khi máy quét
            xong bạn vẫn sửa và bổ sung được, không mất thêm tiền.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Bước hỏi CUỐI — chỉ mở ra sau khi bàn tay đã được đọc xong.
 *
 * Thứ tự này là chủ ý: câu hỏi, lá số và quẻ đều không cần cho việc soi ảnh, mà hỏi
 * chúng trước là bắt người xem khai một loạt thứ trong khi họ chưa biết máy đọc ra
 * được gì. Ngày sinh chỉ hiện khi họ CHỦ ĐỘNG bật đối chiếu Tử Vi – Bát Tự; tắt là
 * lượt xem thuần bàn tay, và bài luận bỏ hẳn mục đối chiếu thay vì bịa một lá số.
 *
 * Thẻ bắt buộc khai (`question_required`/`birth_required`) đã hỏi ở bước nhập —
 * hỏi sau ở đó là để người xem trả tiền quét xong mới biết mình thiếu điều kiện.
 */
function PalmWrapUpStep({
  allowQuestion,
  question,
  onQuestionChange,
  divinerId,
  onDivinerChange,
  includeHexagram,
  onToggleHexagram,
  wantCharts,
  onToggleCharts,
  persons,
  onBirthChange,
}: {
  /** `false` với thẻ toàn diện — xem `PalmTopic.question_allowed`. */
  allowQuestion: boolean;
  question: string;
  onQuestionChange: (value: string) => void;
  divinerId: string;
  onDivinerChange: (value: string) => void;
  includeHexagram: boolean;
  onToggleHexagram: () => void;
  wantCharts: boolean;
  onToggleCharts: () => void;
  persons: PersonForm[];
  onBirthChange: (index: number, birth: PersonForm["birth"]) => void;
}) {
  const toggleClass = (on: boolean) =>
    `flex min-h-14 w-full items-center gap-3 rounded-2xl border px-4 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
      on ? "border-primary/55 bg-primary/[0.08]" : "border-gold-soft/45 bg-card/55"
    }`;
  const knobClass = (on: boolean) =>
    `relative h-6 w-11 shrink-0 rounded-full p-1 transition ${on ? "bg-primary" : "bg-muted"}`;

  return (
    <section className="space-y-3 rounded-2xl border border-gold-soft/45 bg-card/55 p-4">
      <div>
        <p className="font-display text-[15px] text-foreground">Bổ sung trước khi luận giải</p>
        <p className="mt-1 text-[11.5px] leading-5 text-muted-foreground">
          {allowQuestion
            ? "Bạn có thể nhập câu hỏi trọng tâm và chọn thêm Tử Vi – Bát Tự hoặc quẻ Kinh Dịch. Bỏ qua các mục này nếu muốn luận thuần theo bàn tay."
            : "Thẻ này không nhận câu hỏi trọng tâm. Bạn có thể chọn thêm Tử Vi – Bát Tự hoặc quẻ Kinh Dịch; bỏ qua nếu muốn luận thuần theo bàn tay."}
        </p>
      </div>

      {/* MỌI thẻ đều có ô này (đổi chiều 13/08/2026 — xem `allows_question` ở BE). Ô
          nằm SAU bước quét nên người xem đã thấy máy đọc được gì rồi mới quyết định có
          nhấn mạnh điều gì hay không; đóng nó lại là bắt họ trả tiền một phiên chat cho
          câu lẽ ra nói được ngay. Nhánh `false` chỉ còn khi DB tắt riêng một thẻ. */}
      {allowQuestion ? (
        <label className="block">
          <span className="mb-1.5 block text-[12px] font-semibold text-foreground">
            Câu hỏi bạn muốn thầy tập trung
          </span>
          <textarea
            value={question}
            onChange={(e) => onQuestionChange(e.target.value.slice(0, 2000))}
            rows={3}
            placeholder="Ví dụ: Sức khoẻ và công việc năm nay của tôi ra sao?"
            className="input-mystic w-full resize-none"
          />
        </label>
      ) : null}

      <div>
        <span className="mb-1.5 block text-[12px] font-semibold text-foreground">Chọn thầy</span>
        <DivinerPicker value={divinerId} onChange={onDivinerChange} compact />
      </div>

      <button
        type="button"
        onClick={onToggleCharts}
        aria-pressed={wantCharts}
        className={toggleClass(wantCharts)}
      >
        <Sparkles
          className={`size-5 shrink-0 ${wantCharts ? "text-primary" : "text-muted-foreground"}`}
        />
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-foreground">
            Thêm đối chiếu Tử Vi – Bát Tự
          </span>
          <span className="mt-0.5 block text-[11px] leading-4 text-muted-foreground">
            Cần ngày sinh. Bỏ qua thì thầy luận thuần theo bàn tay.
          </span>
        </span>
        <span className={knobClass(wantCharts)}>
          <span
            className={`block size-4 rounded-full bg-background transition ${wantCharts ? "translate-x-5" : ""}`}
          />
        </span>
      </button>

      {wantCharts
        ? persons.map((person, index) => (
            <BirthInputForm
              key={index}
              title={
                persons.length > 1
                  ? person.birth.ho_ten.trim() || `Người ${index + 1}`
                  : "Thông tin sinh"
              }
              value={person.birth}
              onChange={(birth) => onBirthChange(index, birth)}
            />
          ))
        : null}

      <button
        type="button"
        onClick={onToggleHexagram}
        aria-pressed={includeHexagram}
        className={toggleClass(includeHexagram)}
      >
        <Sparkles
          className={`size-5 shrink-0 ${includeHexagram ? "text-primary" : "text-muted-foreground"}`}
        />
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-foreground">
            Gieo thêm quẻ Kinh Dịch
          </span>
          <span className="mt-0.5 block text-[11px] leading-4 text-muted-foreground">
            Gieo theo đúng câu hỏi bạn vừa nhập
          </span>
        </span>
        <span className={knobClass(includeHexagram)}>
          <span
            className={`block size-4 rounded-full bg-background transition ${includeHexagram ? "translate-x-5" : ""}`}
          />
        </span>
      </button>
    </section>
  );
}

function FileThumb({ file }: { file?: File }) {
  const [src, setSrc] = useState("");
  useEffect(() => {
    if (!file) {
      setSrc("");
      return;
    }
    const url = URL.createObjectURL(file);
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  return src ? <img src={src} alt="" className="h-full w-full object-cover" /> : <span>—</span>;
}

/**
 * Khung ảnh MƯỢN từ bàn tay đã lưu (chưa chụp trong phiên này nên không có `File`
 * cục bộ) — BE luôn copy ảnh từ người thứ nhất của hồ sơ, nên gọi cố định
 * `personIndex = 0`. Không có nó thì "Bạn có thể xem lại từng ảnh" là lời hứa suông
 * cho mọi khung mượn: `FileThumb` không có gì để render nên luôn hiện "—".
 */
function ProfileSlotThumb({ profileId, slot }: { profileId: string; slot: PalmSlot }) {
  const image = useQuery({
    queryKey: ["palm-profile-image", profileId, slot],
    queryFn: () => getPalmProfileImage(profileId, 0, slot),
  });
  const [src, setSrc] = useState("");
  useEffect(() => {
    if (!image.data) {
      setSrc("");
      return;
    }
    const url = URL.createObjectURL(image.data);
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [image.data]);
  if (image.isLoading) return <Loader2 className="size-3.5 animate-spin text-muted-foreground" />;
  return src ? <img src={src} alt="" className="h-full w-full object-cover" /> : <span>—</span>;
}

function PalmPage() {
  const { reading: readingIdFromUrl, draft: draftIdFromUrl } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, refresh: refreshUser } = useBoideUser();

  const config = useQuery({ queryKey: ["palm-config"], queryFn: getPalmConfig });

  /**
   * Nháp đang bỏ dở — để lượt ĐÃ TRẢ TIỀN không mất khi tải lại trang.
   *
   * Không có nó thì đóng tab sau lúc quét là mất trắng: bản nháp vẫn nằm trong DB
   * kèm bảng đã quét và `price_charged`, nhưng người xem không còn đường nào chạm
   * tới nó — và họ đã trả tiền cho đúng cái bảng ấy.
   *
   * 🔴 Nháp KHÔNG còn hết hạn (14/09/2026) và cũng đã có mặt trong "Lịch sử luận
   * giải". Khối này vẫn giữ vì nó là lời mời đập vào mắt ngay đầu trang — lịch sử
   * thì phải mở hộp thoại mới thấy.
   *
   * ⚠️ `GET /drafts` chỉ trả lượt dở trong **24 giờ qua** (`RECENT_DRAFT_HOURS` bên
   * BE) — LỌC HIỂN THỊ, không phải hạn sống: một lời nhắc nằm ngay đầu trang mà dài
   * ra theo năm tháng thì biến trang bói thành danh sách việc dở dang. Lượt cũ hơn
   * vào bằng "Lịch sử luận giải" (danh sách ấy không có mốc thời gian nào).
   */
  const drafts = useQuery({ queryKey: ["palm-drafts"], queryFn: listPalmDrafts });

  const resume = useMutation({
    mutationFn: async (draftId: string) => {
      const found = await getPalmDraft(draftId);
      // Đã quét rồi thì gọi lại `scanPalmDraft` để lấy bảng đầy đủ — call này
      // KHÔNG trừ thêm (bản nháp đã có bản quét thì BE trả thẳng bảng), và nó trả về
      // đúng bảng đang có kể cả phần người xem đã sửa.
      const got = found.scan?.done ? await scanPalmDraft(draftId, {}) : null;
      return { draft: found, scan: got && !isPalmJob(got) ? got : null };
    },
    onSuccess: ({ draft: found, scan: scanned }) => {
      setDraft(found);
      setScan(scanned);
      // Việc nền đang chạy cũng vào thẳng màn quét: `PalmScanStep` sẽ hiện tiến độ
      // thay vì nút mời bấm. Không có nhánh này thì người xem tải lại trang giữa lúc
      // thầy đang luận sẽ rơi về màn nhập liệu của một lượt họ đã trả tiền.
      setScanPhase(Boolean(scanned) || found.job?.state === "running");
      // Câu hỏi đã lưu trong nháp (thẻ bắt buộc nêu câu hỏi khai ở bước đầu) — không
      // khôi phục thì ô câu hỏi mở ra trống trơn và người xem tưởng mình chưa hỏi gì.
      if (found.question) setQuestion(found.question);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Không mở lại được lượt xem"),
  });

  /**
   * `?draft=` — đích của push "đã đọc xong bàn tay" (v92): mở thẳng đúng lượt ấy.
   *
   * Ref chứ không phải deps trên `resume`: mutation là object mới mỗi render, để nó
   * trong deps là mỗi render một lần gọi. Và chỉ chạy MỘT lần cho mỗi id — người xem
   * bấm huỷ nháp xong không được bị kéo ngược trở lại nó.
   */
  const resumedFromUrl = useRef("");
  useEffect(() => {
    if (!draftIdFromUrl || resumedFromUrl.current === draftIdFromUrl) return;
    resumedFromUrl.current = draftIdFromUrl;
    resume.mutate(draftIdFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftIdFromUrl]);

  // Thẻ chủ đề: rỗng nghĩa là BE chưa chạy migration v89 → form toàn diện như trước,
  // KHÔNG phải lỗi. `retry: false` để hub không nhấp nháy khi endpoint chưa có.
  const topicsQuery = useQuery({
    queryKey: ["palm-topics"],
    queryFn: listPalmTopics,
    retry: false,
  });
  // useMemo, không `?? []` trần: một mảng mới mỗi render làm deps của useEffect dưới
  // đổi liên tục (eslint react-hooks bắt đúng chỗ này).
  const topics = useMemo(() => topicsQuery.data ?? [], [topicsQuery.data]);
  const [topicSlug, setTopicSlug] = useState("");
  const topic = topics.find((t) => t.slug === topicSlug) ?? null;

  // Chọn sẵn thẻ đầu tiên khi hub vừa về: không để người xem đứng trước một danh
  // sách chưa chọn gì mà nút bói vẫn sáng — họ sẽ không biết mình đang mua thẻ nào.
  useEffect(() => {
    if (!topicSlug && topics.length) setTopicSlug(topics[0].slug);
  }, [topics, topicSlug]);

  const maxPersons = Math.min(
    config.data?.max_persons ?? 5,
    topic?.max_persons ?? config.data?.max_persons ?? 5,
  );
  const minPersons = topic?.min_persons ?? 1;
  const basePrice = topic?.price ?? config.data?.price ?? 0;
  const extraPrice = topic?.extra_person_price ?? config.data?.extra_person_price ?? 0;
  const extraImagePrice = topic?.extra_image_price ?? 0;

  const [persons, setPersons] = useState<PersonForm[]>([emptyPerson(false)]);
  const [images, setImages] = useState<PalmImageMap>(new Map());
  const [question, setQuestion] = useState("");
  const [includeHexagram, setIncludeHexagram] = useState(false);
  const [divinerId, setDivinerId] = useState("cu_an");
  // "Thêm đối chiếu Tử Vi – Bát Tự" ở bước cuối. Mặc định TẮT: người xem chỉ phải
  // khai ngày sinh khi họ chủ động muốn phần đối chiếu, không phải để đi tiếp.
  const [wantCharts, setWantCharts] = useState(false);
  const [fresh, setFresh] = useState<PalmReading | null>(null);
  const [draft, setDraft] = useState<PalmDraft | null>(null);
  // Bảng 16 tiêu chí + cờ "đang ở bước đọc bàn tay". Tách hai state vì bước đọc mở
  // ra TRƯỚC khi có bảng (màn mời bấm, nói rõ đây là chỗ trừ tiền); `scan` chỉ có
  // sau khi máy đọc xong, và giữ lại để quay lại sửa nét rồi vào lại không mất bảng.
  const [scan, setScan] = useState<PalmScanResult | null>(null);
  const [scanPhase, setScanPhase] = useState(false);
  const [shareForTraining, setShareForTraining] = useState(false);
  const [inputStep, setInputStep] = useState<PalmInputStep>("profile");
  const [activePersonIndex, setActivePersonIndex] = useState(0);
  const [editingProfile, setEditingProfile] = useState(true);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // Tự điền ô đầu từ hồ sơ (vẫn sửa được); chỉ chạy một lần khi hồ sơ về.
  const prefilled = useRef(false);
  useEffect(() => {
    if (prefilled.current || !user?.profile) return;
    const value = birthValueFromProfile(user.profile, displayName(user, ""));
    if (!value) return;
    prefilled.current = true;
    // Hồ sơ là ngày sinh THẬT do chính họ khai trước đó → tính là đã khai.
    // `birthValueFromProfile` trả null khi hồ sơ chưa có ngày sinh, nên không có
    // đường nào cờ này được bật bởi một giá trị mặc định.
    setPersons((cur) => [{ ...cur[0], birth: value, birthTouched: true }, ...cur.slice(1)]);
    setEditingProfile(false);
  }, [user]);

  const saved = useQuery({
    queryKey: ["palm-reading", readingIdFromUrl],
    queryFn: () => getPalmReading(readingIdFromUrl as string),
    enabled: Boolean(readingIdFromUrl) && !fresh,
  });

  // Bàn tay đã lưu: chỉ dùng ở ô chọn nguồn ảnh cho từng người trong bước chụp.
  const savedProfiles = usePalmProfiles();

  const resolvedHandOf = (person: PersonForm | undefined): PalmHand => {
    if (!person) return "left";
    return person.hand ?? (person.birth.gioi_tinh === "nam" ? "left" : "right");
  };

  /** Khung ảnh TỐI THIỂU của người thứ `i`, sau khi áp lựa chọn tay của chính họ. */
  const requiredSlotsFor = (i: number) => requiredSlotsOf(topic, resolvedHandOf(persons[i]));

  /** Khung ảnh bàn tay đã lưu của người thứ `i` đang CÓ (mượn được, khỏi chụp). */
  const borrowedSlotsFor = (i: number): PalmSlot[] => {
    const id = persons[i]?.profileId;
    if (!id) return [];
    const profile = (savedProfiles.data?.profiles ?? []).find((p) => p.profile_id === id);
    return (profile?.persons?.[0]?.slots ?? []) as PalmSlot[];
  };

  /**
   * Khung người xem THỰC SỰ phải chụp = thẻ yêu cầu trừ đi khung mượn được.
   *
   * ⚠️ Tách hẳn khỏi `requiredSlotsFor`: mảng RỖNG ở đây nghĩa là "không phải chụp
   * tấm nào", còn `requiredSlotsFor` rỗng nghĩa là "thẻ không khai khung → đủ 4"
   * (`hasAllPalmImages`, `GuidedHandCapture` đều hiểu theo nghĩa thứ hai). Gộp hai
   * khái niệm vào một hàm là mượn đủ 4 ảnh xong lại bị đòi chụp đủ 4.
   */
  const slotsToShootFor = (i: number): PalmSlot[] => {
    const wanted = requiredSlotsFor(i) ?? PALM_SLOTS;
    const have = borrowedSlotsFor(i);
    return wanted.filter((slot) => !have.includes(slot));
  };
  /**
   * Số ảnh thẻ này YÊU CẦU mỗi người. Đếm theo khung thực tế (thẻ cho chọn tay thì
   * mỗi người một danh sách), không theo `topic.slots` là danh sách của tay mặc định
   * — và tuyệt đối không cứng hoá 4, vì thẻ "Xem nhanh" chỉ cần 1 ảnh.
   */
  const requiredCount = requiredSlotsFor(0)?.length ?? PALM_SLOTS.length;
  const totalRequiredImages = persons.reduce(
    (sum, _p, i) => sum + (requiredSlotsFor(i)?.length ?? PALM_SLOTS.length),
    0,
  );
  const activePerson = persons[activePersonIndex] ?? persons[0];
  const activePersonName = activePerson?.birth.ho_ten.trim() || `người ${activePersonIndex + 1}`;
  const activeRequiredSlots = activePerson
    ? (requiredSlotsFor(activePersonIndex) ?? PALM_SLOTS)
    : PALM_SLOTS;
  const activeBorrowedSlots = activePerson ? borrowedSlotsFor(activePersonIndex) : [];
  const activeReadyImagesCount = activeRequiredSlots.filter(
    (slot) =>
      activeBorrowedSlots.includes(slot) || images.has(palmImageField(activePersonIndex, slot)),
  ).length;
  // Đếm đúng số mục dialog chỉnh được — con số này phải khớp danh sách in trên thẻ
  // ("Tay chính · Vân đầu ngón · Móng tay"), nếu không thì "Đã thêm 2/3" đếm một tập
  // khác với tập người xem đang nhìn. Quẻ Kinh Dịch đã rời khỏi dialog này (hỏi ở
  // bước sau bảng, cùng câu hỏi), nên nó cũng phải rời khỏi phép đếm.
  const advancedSelectionCount = activePerson
    ? [
        activePerson.thumb,
        Object.keys(activePerson.fingerprints).length > 0,
        Object.values(activePerson.nail).some(Boolean),
      ].filter(Boolean).length
    : 0;
  // Đủ ảnh của người đang nhập chưa — tính trên khung PHẢI CHỤP (đã trừ ảnh mượn từ
  // bàn tay đã lưu). Mảng rỗng = không phải chụp gì, tức đã sẵn sàng.
  const activePersonImagesReady = activePerson
    ? slotsToShootFor(activePersonIndex).every((slot) =>
        images.has(palmImageField(activePersonIndex, slot)),
      )
    : false;

  useEffect(() => {
    if (activePersonIndex > persons.length - 1)
      setActivePersonIndex(Math.max(0, persons.length - 1));
  }, [activePersonIndex, persons.length]);

  // Synchronize editingProfile when activePersonIndex changes
  const prevActiveIndex = useRef(activePersonIndex);
  useEffect(() => {
    if (prevActiveIndex.current !== activePersonIndex) {
      prevActiveIndex.current = activePersonIndex;
      const person = persons[activePersonIndex];
      if (person) {
        setEditingProfile(!isBirthValueComplete(person.birth));
      }
    }
  }, [activePersonIndex, persons]);

  /**
   * Thẻ cần nhiều người → MỞ SẴN đủ ô nhập.
   *
   * "Đối chiếu hai bàn tay hai người" mà màn hình chỉ có một ô thì người xem không
   * có chỗ nào khai bàn tay thứ hai — nút "Xem thêm người" nằm tít dưới card giá và
   * trông như một tuỳ chọn, không phải điều kiện của thẻ. Trước đây họ chỉ biết khi
   * đọc dòng chặn ở nút bói.
   *
   * Ô mở ở đây mang cờ `autoAdded` để effect dưới biết đường đóng lại khi đổi thẻ.
   *
   * Mở xong thì ĐƯA LUÔN người xem tới ô mới, nếu những ô đang có đã đủ thông tin
   * (ô "Bạn" thường được điền sẵn từ hồ sơ). Không có bước nhảy này thì màn hình
   * đứng ở tấm thẻ tóm tắt của người 1 với nút "Bắt đầu chụp ảnh" xám ngắt, còn
   * việc phải làm — khai người 2 — nằm sau một tab mà không có gì chỉ vào.
   */
  useEffect(() => {
    if (persons.length >= minPersons) return;
    const firstOpened = persons.length;
    setPersons((cur) =>
      cur.length >= minPersons
        ? cur
        : [...cur, ...Array.from({ length: minPersons - cur.length }, () => emptyPerson(true))],
    );
    if (persons.every((p) => isBirthValueComplete(p.birth))) setActivePersonIndex(firstOpened);
  }, [minPersons, persons]);

  /**
   * Đổi sang thẻ cần ít người hơn → ĐÓNG LẠI các ô do thẻ trước tự mở ra.
   *
   * Không có bước này thì ô người thứ hai của "Đối chiếu hai bàn tay hai người" còn
   * nguyên sau khi quay về thẻ một người: màn hình vẫn đòi đủ ảnh của hai người, và
   * người xem không hiểu vì sao thẻ "Xem nhanh một bàn tay" lại bắt chụp 8 tấm.
   *
   * Chỉ đụng tới ô `autoAdded`, tức thứ CHÍNH thẻ mở ra và chính thẻ mới không cần
   * nữa. Người xem tự bấm "Xem thêm người" thì ô đó là ý định của họ, đổi thẻ không
   * được phép xoá — `blockedReason` sẽ nhắc "thẻ này xem tối đa N người" nếu thừa.
   *
   * CHỈ chạy khi `topicSlug` đổi (chốt bằng ref): chạy theo `images`/`persons` thì ô
   * vừa tự thêm cũng bị cuốn theo ngay lần render kế tiếp.
   */
  const trimmedForTopic = useRef(topicSlug);
  useEffect(() => {
    if (trimmedForTopic.current === topicSlug) return;
    trimmedForTopic.current = topicSlug;

    // Duyệt từ CUỐI: `reindexPalmImages` dồn index về sau mỗi lần xoá, bỏ ô có index
    // lớn trước thì các ô còn lại giữ nguyên số thứ tự đang dùng làm khoá ảnh.
    const dropped: number[] = [];
    let remaining = persons.length;
    for (let i = persons.length - 1; i >= 0 && remaining > minPersons; i -= 1) {
      if (!persons[i].autoAdded) continue;
      dropped.push(i);
      remaining -= 1;
    }
    if (!dropped.length) return;

    setPersons((cur) => cur.filter((_, i) => !dropped.includes(i)));
    setImages((cur) => {
      let next = cur;
      let count = persons.length;
      for (const i of dropped) {
        next = reindexPalmImages(next, i, count);
        count -= 1;
      }
      return next;
    });
    // Ô rỗng thì đóng lặng lẽ. Ô đã có tên/ảnh mà biến mất không một lời nào là để
    // người xem tự phát hiện ra mình vừa mất công chụp — phải nói ra.
    if (dropped.some((i) => personHasInput(persons[i], i, images))) {
      toast.info(`Thẻ này chỉ xem ${minPersons} người — đã bỏ phần đã nhập của người còn lại.`);
    }
  }, [images, minPersons, persons, topicSlug]);

  /** Số ảnh đã chụp VƯỢT mức tối thiểu của từng người — cơ sở tính phụ phí. */
  const extraImagesCount = useMemo(() => {
    let n = 0;
    for (let i = 0; i < persons.length; i += 1) {
      const required = requiredSlotsFor(i);
      const wanted = required?.length ? required : PALM_SLOTS;
      for (const slot of PALM_SLOTS) {
        if (!wanted.includes(slot) && images.has(palmImageField(i, slot))) n += 1;
      }
    }
    return n;
  }, [images, persons, topic]);

  const price =
    basePrice + Math.max(0, persons.length - 1) * extraPrice + extraImagesCount * extraImagePrice;
  // KHÔNG dùng `hasAllPalmImages` nữa: nó hiểu mảng rỗng là "đủ 4 khung", đúng cho
  // thẻ không khai khung nhưng sai hẳn cho người mượn đủ ảnh từ bàn tay đã lưu
  // (rỗng ở đây nghĩa là không phải chụp tấm nào).
  const imagesReady = persons.every((_p, i) =>
    slotsToShootFor(i).every((slot) => images.has(palmImageField(i, slot))),
  );
  const namesReady = persons.every((p) => isBirthValueComplete(p.birth));
  const namedPersonsCount = persons.filter((p) => isBirthValueComplete(p.birth)).length;
  /**
   * Ô người TIẾP THEO còn thiếu họ tên — đích của nút chính ở bước hồ sơ.
   *
   * `-1` nghĩa là từ đây trở đi không còn ai thiếu, tức nút chính quay về đúng việc
   * của nó là đi tới bước chụp ảnh.
   *
   * Cố ý KHÔNG tự nhảy theo giá trị ô nhập: `isBirthValueComplete` bật ngay ở ký tự
   * họ tên đầu tiên, nhảy theo nó là giật form khỏi tay người đang gõ dở.
   */
  const nextPersonNeedingInfo = persons.findIndex(
    (p, i) => i > activePersonIndex && !isBirthValueComplete(p.birth),
  );
  const goesToNextPerson =
    Boolean(activePerson && isBirthValueComplete(activePerson.birth)) && nextPersonNeedingInfo > -1;
  const personsReady = persons.length >= minPersons && persons.length <= maxPersons;
  // Hai thứ chỉ hỏi TRƯỚC khi trừ tiền khi thẻ bắt buộc. Thẻ để tuỳ chọn thì chúng
  // xuống bước sau bảng — bắt khai trước rồi mới cho biết máy đọc ra gì là hỏi ngược
  // nhịp, còn hỏi sau với thẻ BẮT BUỘC thì người xem trả tiền quét xong mới biết mình
  // không đủ điều kiện dùng thẻ.
  // Thẻ TOÀN DIỆN không hỏi câu hỏi ở đâu cả: bài viết đủ 9 mục nên không có trọng
  // tâm nào để chọn, và người xem hỏi thêm được ở phiên chat sau khi đã có bài.
  // BE cũ không trả cờ → `true`, tức hành vi cũ.
  const questionAllowed = topic ? (topic.question_allowed ?? true) : true;
  const askQuestionUpfront = questionAllowed && Boolean(topic?.config?.question_required);
  const askBirthUpfront = Boolean(topic?.config?.birth_required);
  const questionReady = !askQuestionUpfront || question.trim().length > 0;
  // Thẻ BẮT BUỘC khai sinh thì giờ sinh cũng bắt buộc: lá số thiếu giờ là lá số
  // dựng trên giả định. Người chọn "chỉ xem tay" (`skipBirth`) không lập lá số nào
  // nên không bị hỏi giờ.
  const birthReady =
    !askBirthUpfront || persons.every((p) => p.skipBirth || isBirthHourSet(p.birth));
  const canStart =
    imagesReady && namesReady && personsReady && questionReady && birthReady && !config.isLoading;

  useEffect(() => {
    if (namesReady && inputStep === "profile" && !editingProfile) return;
    if (!namesReady && inputStep !== "profile") setInputStep("profile");
  }, [editingProfile, inputStep, namesReady]);

  /**
   * Dựng payload người xem cho bước TẠO NHÁP (trước khi quét).
   *
   * Chỉ gửi những gì tầng quét cần: tên, GIỚI TÍNH (mục 1 tiên thiên/hậu thiên),
   * nghi thức chắp tay, tay thuận, tay được chụp, vân/móng nếu đã khai. Ngày–giờ
   * sinh chỉ đi kèm khi thẻ BẮT BUỘC khai; còn lại chúng được hỏi ở bước cuối, sau
   * khi bàn tay đã đọc xong (`readingExtras`).
   *
   * Ba chỗ cố ý gửi `undefined` thay vì một con số cho lành:
   * - không khai sinh ở bước này → bỏ trống cả ngày–tháng–năm–giờ, và
   *   `use_profile: false`. Gửi `use_profile` khi chưa ai hỏi ngày sinh là lấy ngày
   *   trong hồ sơ ra dùng, tức lập lá số cho một người chưa hề chọn điều đó.
   * - `skipBirth` → y như trên, cho thẻ có hỏi mà người xem chọn "chỉ xem tay".
   * - `gio === -1` (không rõ giờ) → `gio: undefined`. BE cần phân biệt "không biết
   *   giờ" với "sinh 12h trưa": nó vẫn lập lá số bằng giả định giờ Ngọ nhưng nói rõ
   *   trụ giờ và cung Thân là chưa chắc. Gửi thẳng 12 như trước là bịa một dữ kiện.
   */
  const personsPayload = (): PalmPersonInput[] =>
    persons.map((p, i) => {
      const omitBirth = !askBirthUpfront || p.skipBirth;
      return {
        ho_ten: p.birth.ho_ten.trim(),
        nam: omitBirth ? undefined : p.birth.nam,
        thang: omitBirth ? undefined : p.birth.thang,
        ngay: omitBirth ? undefined : p.birth.ngay,
        gio: omitBirth || p.birth.gio < 0 ? undefined : p.birth.gio,
        phut: 0,
        // Giới tính luôn gửi, kể cả lượt chỉ xem tay: tầng quét dùng nó cho mục 1 và
        // cho quy ước nam tả nữ hữu khi người xem không làm nghi thức chắp tay.
        gioi_tinh: p.birth.gioi_tinh,
        is_lunar: p.birth.is_lunar,
        is_leap_month: !omitBirth && p.birth.is_lunar && !!p.birth.is_leap_month,
        dominant_thumb: p.thumb,
        thuan_tay: p.dominantHand,
        hand: topic?.hand_choice ? resolvedHandOf(p) : undefined,
        // BE đọc ảnh của hồ sơ này rồi COPY vào lượt mới; khung nào hồ sơ không có
        // thì người xem đã chụp bù ở bước trước.
        profile_id: p.profileId ?? undefined,
        van_dau_ngon: p.fingerprints,
        dac_diem_mong: p.nail,
        use_profile: askBirthUpfront && i === 0 && !p.skipBirth,
      };
    });

  /**
   * Phần payload của bước LUẬN — hỏi sau khi bàn tay đã đọc xong.
   *
   * Ném lỗi khi người xem bật đối chiếu lá số mà bỏ trống ngày sinh: BE cũng chặn,
   * nhưng chặn ở đây thì họ thấy ngay tại ô mình vừa bỏ trống.
   */
  const readingExtras = () => {
    if (wantCharts) {
      // `birthTouched` chứ không phải "ô có giá trị": form khởi tạo sẵn 01/01/2000
      // nên mọi ô luôn có giá trị, và gửi nó đi là lập lá số cho một ngày sinh chưa
      // ai khai.
      // Giờ sinh tính vào "đã khai": nó dựng trụ giờ Bát Tự và cung Thân Tử Vi.
      const thieu = persons.findIndex((p) => !p.birthTouched || !isBirthHourSet(p.birth));
      if (thieu >= 0) {
        throw new Error(
          `Bạn đang bật đối chiếu lá số — khai ngày và giờ sinh cho ${
            persons[thieu].birth.ho_ten.trim() || `người ${thieu + 1}`
          } hoặc tắt lựa chọn này.`,
        );
      }
    }
    return {
      // Thẻ bị tắt ô câu hỏi (DB khai riêng) → không gửi gì, kể cả chuỗi còn sót lại
      // trong state từ một thẻ khác người xem vừa bấm qua.
      question: questionAllowed ? question.trim() || undefined : undefined,
      include_hexagram: includeHexagram,
      diviner_id: divinerId,
      // Không bật đối chiếu = không gửi gì cả: nháp vốn không có thông tin sinh, nên
      // "không gửi" đúng là "chỉ xem tay".
      persons: wantCharts
        ? persons.map((p, i) => ({
            person_index: i,
            ho_ten: p.birth.ho_ten.trim() || undefined,
            nam: p.birth.nam,
            thang: p.birth.thang,
            ngay: p.birth.ngay,
            gio: p.birth.gio < 0 ? undefined : p.birth.gio,
            phut: 0,
            gioi_tinh: p.birth.gioi_tinh,
            is_lunar: p.birth.is_lunar,
            is_leap_month: p.birth.is_lunar && !!p.birth.is_leap_month,
          }))
        : undefined,
    };
  };

  // Luồng soát bảng, KHÔNG phải model vẽ nét (xem ghi chú đầu file). Lùi về cờ
  // `segmentation` chỉ để BE cũ (chưa có `scan_review`) vẫn chạy đúng như trước.
  const reviewEnabled =
    config.data?.scan_review?.enabled ?? config.data?.segmentation?.enabled ?? false;

  // BE quyết định có chặn hay không (`blocked`), FE chỉ dựng màn. Chưa tải xong thì
  // KHÔNG chặn — chớp một màn đồng ý rồi tự biến mất còn khó hiểu hơn là chờ.
  const consent = usePalmConsent();
  const consentBlocked = consent.data?.blocked ?? false;

  // BE cũ không trả khối `birth` → giữ đúng hành vi trước đây (bắt buộc khai ngày sinh).
  const birthOptional = config.data?.birth?.optional ?? false;
  const birthNote =
    config.data?.birth?.note ??
    "Khai ngày sinh để thầy đối chiếu bàn tay với lá số Tử Vi và Bát Tự.";

  const finishReading = (res: PalmReading) => {
    setDraft(null);
    setScan(null);
    setScanPhase(false);
    setFresh(res);
    navigate({ to: "/boi-chi-tay", search: { reading: res.reading_id }, replace: true });
    void refreshUser();
    void queryClient.invalidateQueries({ queryKey: ["palm-readings"] });
    // Bài luận vừa xong cũng mở một phiên đàm đạo → trang Lịch sử có thêm một dòng.
    void queryClient.invalidateQueries({ queryKey: ["boide-sessions"] });
    // Nháp vừa thành bài luận → thẻ "lượt đang dở" phải biến mất, nếu không người
    // xem bấm "Mở lại" vào một nháp không còn tồn tại.
    void queryClient.invalidateQueries({ queryKey: ["palm-drafts"] });
    toast.success(
      res.price_charged > 0 ? `Đã xem xong · trừ ${formatVnd(res.price_charged)}` : "Đã xem xong",
    );
  };

  /**
   * Bắt đầu lượt xem: tạo nháp rồi QUÉT LUÔN trong cùng một lần bấm.
   *
   * Quét dời lên ngay sau khi thêm ảnh (thay vì sau bước duyệt nét như trước) vì
   * đó là thứ tự đúng của việc: soi ảnh trước, rồi mới biết CÁI GÌ CÒN THIẾU để
   * mời người xem bổ sung — móng, vân đầu ngón, đường chỉ phụ. Duyệt nét không mất
   * đi, nó chuyển thành một trong những cách bổ sung ở màn sau.
   *
   * ⚠️ Vì thế nút này TRỪ TIỀN. Nhãn nút phải nói ra giá, và `blockedReason` phải
   * chặn hết mọi thiếu sót trước đó — bấm nhầm ở đây là mất tiền thật.
   *
   * BE tắt bước nháp (hoặc chưa có v88) → giữ đúng luồng cũ một call, để tính năng
   * không chết theo một model phụ.
   */
  const start = useMutation({
    mutationFn: async () => {
      const payload = {
        persons: personsPayload(),
        question: question.trim() || undefined,
        include_hexagram: includeHexagram,
        diviner_id: divinerId,
        topic_slug: topicSlug || undefined,
      };
      if (!reviewEnabled) {
        return { kind: "reading" as const, reading: await startPalmReading(payload, images) };
      }
      const created = await createPalmDraft(payload, images);
      // Nhận việc quét ngay. Nháp vừa tạo còn miễn phí; chính call này mới trừ tiền,
      // và claim ở BE là một câu UPDATE nên bấm lại không trừ lần hai.
      //
      // Trả về `PalmJob` (việc chạy nền) chứ hầu như không bao giờ là bảng — bảng chỉ
      // có ngay khi bản nháp này đã quét từ trước, mà nháp vừa tạo thì chưa.
      const started = await scanPalmDraft(created.draft_id, {
        question: question.trim() || undefined,
        share_for_training: shareForTraining,
        consent_version: created.consent_document.version,
      });
      // Ghi sổ để `PalmJobWatcher` báo được kể cả khi người xem rời trang trong lúc
      // chờ — đúng cái ta vừa mời họ làm trên màn hình chờ.
      if (isPalmJob(started)) rememberPalmJob(created.draft_id, "scan");
      return {
        kind: "draft" as const,
        draft: created,
        scan: isPalmJob(started) ? null : started,
      };
    },
    onSuccess: (res) => {
      if (res.kind === "draft") {
        setDraft(res.draft);
        setScan(res.scan);
        setScanPhase(true);
        // `scan` rỗng = việc quét vừa được nhận và đang chạy nền; `PalmScanStep` tự
        // hỏi thăm rồi lấy bảng khi xong, nên ở đây không có gì để đếm.
        if (!res.scan) return;
        // Đếm DẤU HIỆU máy đọc ra, không đếm "ô còn thiếu": danh sách dấu hiệu
        // không có ô trống nào cả — máy không thấy thì đơn giản là không ghi.
        const found = res.scan.persons.reduce(
          (sum, p) => sum + p.groups.reduce((n, g) => n + g.rows.length, 0),
          0,
        );
        if (found) {
          toast.info(`Đã đọc xong — ${found} dấu hiệu trên tay bạn. Soát lại giúp nhé.`);
        }
        return;
      }
      finishReading(res.reading);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Không đọc được bàn tay"),
    // 🔴 Lượt này VỪA TRỪ TIỀN (cả đường một-cú lẫn đường nháp: `scanPalmDraft` là
    // chỗ thu tiền). Không đọc lại hồ sơ thì số dư và hạn mức đứng im tới khi người
    // dùng F5 — và `refreshBoideUser()` cũng chính là thứ bắn sự kiện cho
    // `useQuotaStatus`. `onSettled` vì lỗi SAU khi đã trừ tiền là ca BE có tính đến.
    onSettled: () => void refreshBoideUser(),
  });

  const result = fresh ?? saved.data ?? null;

  const addPerson = () => {
    if (persons.length >= maxPersons) return;
    const nextIndex = persons.length;
    setPersons((cur) => [...cur, emptyPerson(false)]);
    setActivePersonIndex(nextIndex);
    setEditingProfile(true);
    setInputStep("profile");
  };

  const removePerson = (index: number) => {
    setImages((cur) => reindexPalmImages(cur, index, persons.length));
    setPersons((cur) => cur.filter((_, i) => i !== index));
    setActivePersonIndex((current) =>
      current > index ? current - 1 : Math.min(current, persons.length - 2),
    );
  };

  const startOver = () => {
    setFresh(null);
    setDraft(null);
    setScan(null);
    setScanPhase(false);
    setImages(new Map());
    setQuestion("");
    setInputStep("profile");
    setActivePersonIndex(0);
    setEditingProfile(false);
    navigate({ to: "/boi-chi-tay", search: {}, replace: true });
  };

  /** Huỷ nháp: xoá luôn ở BE để ảnh bàn tay không nằm lại chờ hết hạn. */
  /**
   * Huỷ nháp: xoá luôn ở BE để ảnh bàn tay không nằm lại chờ hết hạn.
   *
   * ⚠️ CHỈ khi chưa trả tiền. Từ lúc máy đọc bàn tay xong (`scan` có giá trị) thì
   * lượt này ĐÃ TRỪ TIỀN — xoá nháp là xoá luôn thứ họ vừa mua mà chưa kịp nhận bài
   * luận, và không có đường hoàn. Khi đó đưa họ về bảng để bấm luận cho xong.
   */
  const cancelDraft = () => {
    if (scan) {
      setScanPhase(true);
      toast.info("Lượt này đã trừ tiền ở bước đọc bàn tay — bấm “Luận tướng tay” để nhận bài.");
      return;
    }
    const id = draft?.draft_id;
    setDraft(null);
    setScanPhase(false);
    if (id) void deletePalmDraft(id).catch(() => undefined);
    void queryClient.invalidateQueries({ queryKey: ["palm-drafts"] });
  };

  const blockedReason = useMemo(() => {
    if (!namesReady) return "Điền họ tên cho từng người để tiếp tục";
    if (persons.length < minPersons)
      return `Thẻ này cần ít nhất ${minPersons} người — thêm người để tiếp tục`;
    if (persons.length > maxPersons) return `Thẻ này xem tối đa ${maxPersons} người`;
    if (!imagesReady) return `Chụp đủ ${requiredCount} ảnh bàn tay cho mỗi người`;
    if (!questionReady) return "Thẻ này cần bạn nêu câu hỏi cụ thể";
    if (!birthReady) return "Thẻ này cần thông tin sinh để đối chiếu lá số";
    return "";
  }, [
    birthReady,
    imagesReady,
    maxPersons,
    minPersons,
    namesReady,
    persons.length,
    questionReady,
    requiredCount,
  ]);

  /**
   * Chuyển sang ô người khác VÀ kéo màn hình về đúng chỗ phải nhập.
   *
   * Nút "Tiếp tục · Người 2" nằm ở ĐÁY khối hồ sơ, còn ô họ tên của người 2 nằm ở
   * đầu khối. Đổi người mà không kéo thì màn hình đứng nguyên chỗ cũ: người xem vừa
   * bấm một nút và thấy y hệt cảnh cũ, không có gì cho biết là đã sang người khác.
   */
  const personFormRef = useRef<HTMLElement>(null);
  const focusPerson = (index: number) => {
    setActivePersonIndex(index);
    const person = persons[index];
    const isComplete = person ? isBirthValueComplete(person.birth) : false;
    setEditingProfile(!isComplete);
    // rAF: đợi React vẽ xong ô của người mới rồi mới đo vị trí.
    requestAnimationFrame(() => {
      const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      personFormRef.current?.scrollIntoView({
        behavior: reduced ? "auto" : "smooth",
        block: "start",
      });
    });
  };

  const updateActivePerson = (patch: Partial<PersonForm>) => {
    setPersons((cur) =>
      cur.map((item, index) => (index === activePersonIndex ? { ...item, ...patch } : item)),
    );
  };

  const goToStep = (next: PalmInputStep) => {
    if (next === "capture" && namesReady) {
      setEditingProfile(false);
      setInputStep(next);

      // Find the first person who hasn't captured all photos yet
      const firstPersonNeedingImages = persons.findIndex(
        (_person, index) =>
          !slotsToShootFor(index).every((slot) => images.has(palmImageField(index, slot))),
      );
      const targetIndex = firstPersonNeedingImages >= 0 ? firstPersonNeedingImages : 0;
      setActivePersonIndex(targetIndex);

      // Scroll to the capture section
      requestAnimationFrame(() => {
        const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        personFormRef.current?.scrollIntoView({
          behavior: reduced ? "auto" : "smooth",
          block: "start",
        });
      });
    }
    if (next === "finish" && canStart) {
      setInputStep(next);
    }
    if (next === "profile") setInputStep(next);
  };

  if (start.isPending) {
    return (
      <Shell>
        {reviewEnabled ? (
          <div className="flex flex-col items-center gap-4 rounded-2xl border border-gold-soft/45 bg-card/55 px-6 py-14 text-center backdrop-blur">
            <Loader2 className="size-8 animate-spin text-primary" />
            <p className="font-display text-base text-primary">Máy đang đọc bàn tay bạn</p>
            <p className="max-w-xs text-[12px] leading-5 text-muted-foreground">
              Máy đang phân tích hình ảnh và nhận diện các đường nét trên bàn tay của bạn. Sau khi
              quét xong, bạn có thể tự kiểm tra, điều chỉnh hoặc bổ sung thêm thông tin trước khi
              gửi thầy luận giải.
            </p>
          </div>
        ) : (
          <PalmLoading />
        )}
      </Shell>
    );
  }

  if (draft) {
    return (
      <Shell className="space-y-4">
        <header className="flex items-start gap-3">
          {/* Đang ở vòng phụ vẽ nét → nút lùi ngay trên đầu. Nút ở đáy màn có thể
              nằm dưới rất nhiều nội dung; người bấm nhầm vào đây cần thấy đường ra
              ngay lập tức chứ không phải cuộn đi tìm. */}
          {!scanPhase && scan ? (
            <button
              type="button"
              onClick={() => setScanPhase(true)}
              aria-label="Quay lại bảng"
              className="flex size-11 shrink-0 items-center justify-center rounded-2xl border border-primary/25 bg-card text-primary transition hover:border-primary/50"
            >
              <ChevronLeft className="size-5" />
            </button>
          ) : (
            <span
              className={`flex size-11 shrink-0 items-center justify-center rounded-2xl border ${
                scanPhase && scan
                  ? "border-emerald-500/30 bg-emerald-500/[0.08] text-emerald-700 dark:text-emerald-300"
                  : "border-primary/20 bg-card text-primary"
              }`}
            >
              {scanPhase && scan ? (
                <CircleCheckBig className="size-6" />
              ) : (
                <IconChiTay className="size-6" />
              )}
            </span>
          )}
          <div className="min-w-0">
            <h1 className="font-display text-[21px] font-bold leading-7 tracking-[-0.025em] text-foreground md:text-2xl">
              {scanPhase
                ? scan
                  ? "Đã quét xong bàn tay"
                  : "Đọc bàn tay"
                : scan
                  ? "Vẽ thêm đường chỉ tay"
                  : "Duyệt nét chỉ tay"}
            </h1>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">
              {scanPhase
                ? scan
                  ? "Kết quả quét đã sẵn sàng. Xem riêng từng người, từng tay rồi tiếp tục để nhận bài luận giải."
                  : "Máy sẽ đọc các đặc điểm bàn tay trước khi thầy luận giải."
                : scan
                  ? "Vẽ xong bấm quay lại là về đúng bảng bạn đang bỏ dở."
                  : "Kiểm tra và xác nhận nét vẽ để thầy luận giải."}
            </p>
          </div>
        </header>
        {scanPhase ? (
          <PalmScanStep
            draft={draft}
            initialScan={scan}
            question={question}
            shareForTraining={shareForTraining}
            beforeActionSlot={
              <PalmWrapUpStep
                allowQuestion={questionAllowed}
                question={question}
                onQuestionChange={setQuestion}
                divinerId={divinerId}
                onDivinerChange={setDivinerId}
                includeHexagram={includeHexagram}
                onToggleHexagram={() => setIncludeHexagram((value) => !value)}
                wantCharts={wantCharts}
                onToggleCharts={() => setWantCharts((value) => !value)}
                persons={persons}
                onBirthChange={(index, birth) =>
                  setPersons((cur) =>
                    cur.map((p, i) => (i === index ? { ...p, birth, birthTouched: true } : p)),
                  )
                }
              />
            }
            readingExtras={readingExtras}
            onScanned={setScan}
            onDone={finishReading}
          />
        ) : (
          <PalmReviewStep
            draft={draft}
            notice={config.data?.segmentation?.notice}
            // Đã quét (tức đã trả tiền) thì màn này là một VÒNG PHỤ mở ra từ bảng,
            // không phải một bước trên đường đi — chữ trên nút phải nói đúng thế.
            returning={Boolean(scan)}
            onNext={(share) => {
              setShareForTraining(share);
              setScanPhase(true);
            }}
            onCancel={() => (scan ? setScanPhase(true) : cancelDraft())}
          />
        )}
      </Shell>
    );
  }

  if (readingIdFromUrl && saved.isLoading && !fresh) {
    return (
      <Shell className="space-y-4">
        <Skeleton className="h-28 w-full rounded-2xl" />
        <Skeleton className="h-72 w-full rounded-2xl" />
      </Shell>
    );
  }

  if (result) {
    return (
      <Shell scrollToTopKey={result.reading_id}>
        <header className="mb-5 flex items-start gap-3">
          <span className="flex size-11 shrink-0 items-center justify-center rounded-2xl border border-primary/20 bg-card text-primary">
            <IconChiTay className="size-6" />
          </span>
          <div className="min-w-0 flex-1">
            <h1 className="font-display text-[21px] font-bold leading-7 tracking-[-0.025em] text-foreground md:text-2xl">
              Tướng tay của bạn
            </h1>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">
              Xem lại bản quét từng tấm ảnh cùng lời luận cho mỗi người.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={startOver} className="shrink-0">
            Xem lượt mới
          </Button>
        </header>
        <PalmReadingView reading={result} />
      </Shell>
    );
  }

  // Chưa đồng ý cho lưu + dùng ảnh bàn tay → dừng ở CỬA VÀO, không dựng form chụp.
  // Hỏi trước khi người ta chụp tấm nào: ảnh bàn tay là dữ liệu sinh trắc, và hỏi
  // sau khi họ đã chụp xong thì vừa mất công vừa không dùng được.
  // Đặt SAU nhánh `result` để bài đã mua vẫn mở đọc lại được — BE không chặn GET,
  // và chặn ở đây là giữ luôn thứ họ đã trả tiền.
  if (consentBlocked) {
    return (
      <Shell className="space-y-4">
        <header className="flex items-start gap-3">
          <span className="flex size-11 shrink-0 items-center justify-center rounded-2xl border border-primary/20 bg-card text-primary">
            <IconChiTay className="size-6" />
          </span>
          <div className="min-w-0">
            <h1 className="font-display text-[21px] font-bold leading-7 tracking-[-0.025em] text-foreground md:text-2xl">
              Bói chỉ tay
            </h1>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">
              Một bước nữa trước khi bắt đầu.
            </p>
          </div>
        </header>
        <PalmConsentGate />
      </Shell>
    );
  }

  return (
    <Shell className="space-y-4">
      <header className="flex items-start gap-3">
        <span className="flex size-11 shrink-0 items-center justify-center rounded-2xl border border-primary/20 bg-card text-primary">
          <IconChiTay className="size-6" />
        </span>
        <div className="min-w-0">
          <h1 className="font-display text-[21px] font-bold leading-7 tracking-[-0.025em] text-foreground md:text-2xl">
            Bói chỉ tay
          </h1>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">
            Chụp ảnh bàn tay để thầy soi các đường và gò chi tiết.
            {birthOptional
              ? " Khai thêm ngày sinh để đối chiếu lá số Tử Vi & Bát Tự."
              : " Kết hợp cùng lá số Tử Vi & Bát Tự để luận giải."}
          </p>
        </div>
      </header>

      {config.data && !config.data.enabled ? (
        <p className="rounded-xl border border-destructive/40 bg-destructive/10 p-3 text-sm text-foreground">
          Tính năng bói chỉ tay đang tạm đóng. Bạn quay lại sau nhé.
        </p>
      ) : null}

      {/* Lượt đang bỏ dở — đứng TRÊN mọi thứ khác, vì lượt ấy có thể đã trả tiền
          rồi. Để dưới thì người xem bắt đầu lại từ đầu và trả tiền lần hai cho
          cùng một bàn tay. */}
      {(drafts.data ?? []).length ? (
        <section className="space-y-2 rounded-2xl border border-primary/35 bg-primary/[0.06] p-3.5">
          <p className="text-[12px] font-semibold text-foreground">Bạn có lượt xem đang dở</p>
          <p className="text-[11px] leading-4 text-muted-foreground">
            Mở lại để xem tiếp bảng và bổ sung — nếu lượt đó đã trả tiền thì mở lại không mất thêm
            đồng nào. Đây là các lượt trong 24 giờ qua; lượt cũ hơn vẫn nằm đủ trong “Lịch sử luận
            giải” bên dưới và không bao giờ hết hạn.
          </p>
          <div className="flex flex-col gap-1.5">
            {(drafts.data ?? []).slice(0, 3).map((item) => (
              <div
                key={item.id}
                className="flex items-center gap-2 rounded-xl border border-border/45 bg-card/45 px-3 py-2"
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[11.5px] font-medium text-foreground">
                    {item.names.filter(Boolean).join(" & ") || `${item.persons_count} người`}
                  </span>
                  {item.question ? (
                    <span className="mt-0.5 block truncate text-[10px] text-muted-foreground">
                      {item.question}
                    </span>
                  ) : null}
                </span>
                <Button
                  size="sm"
                  className="h-8 shrink-0 text-[11px]"
                  disabled={resume.isPending}
                  onClick={() => resume.mutate(item.id)}
                >
                  {resume.isPending ? <Loader2 className="mr-1 size-3.5 animate-spin" /> : null}
                  Mở lại
                </Button>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {/* Mở lại một lượt CŨ HƠN 24 GIỜ (bấm từ "Lịch sử luận giải"): dòng ấy không nằm
          trong thẻ trên, nên vòng xoay của thẻ không hiện ở đâu cả và người xem bấm
          xong thấy trang đứng im vài giây. */}
      {resume.isPending && !(drafts.data ?? []).some((item) => item.id === resume.variables) ? (
        <p className="flex items-center gap-2 text-[11px] text-muted-foreground">
          <Loader2 className="size-3.5 animate-spin" />
          Đang mở lại lượt xem…
        </p>
      ) : null}

      <SavedReadings
        onOpen={(id, isDraft) =>
          navigate({
            to: "/boi-chi-tay",
            search: isDraft ? { draft: id } : { reading: id },
          })
        }
      />

      <InputStepper
        active={inputStep}
        canCapture={namesReady}
        canFinish={canStart}
        onChange={goToStep}
      />

      {inputStep === "profile" ? (
        <PalmTopicPicker topics={topics} value={topicSlug} onChange={setTopicSlug} />
      ) : null}
      {persons.length > 1 && inputStep !== "profile" ? (
        <section className="scroll-mt-16 space-y-3 rounded-2xl border border-gold-soft/40 bg-card/50 p-4 shadow-[0_12px_30px_rgba(77,48,19,0.05)]">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 text-primary">
              <UserRound className="size-3.5 text-primary/80" />
              <p className="text-[11px] font-bold uppercase tracking-[0.1em]">
                Bạn đang nhập cho ai
              </p>
            </div>
            <p className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">
              {namedPersonsCount}/{persons.length} người đã có tên
            </p>
          </div>
          <div
            className={`gap-2 ${
              persons.length <= 3
                ? `grid ${persons.length === 2 ? "grid-cols-2" : "grid-cols-3"}`
                : "flex overflow-x-auto pb-1"
            }`}
            role="tablist"
            aria-label="Người đang xem"
          >
            {persons.map((person, index) => {
              const named = isBirthValueComplete(person.birth);
              const active = activePersonIndex === index;
              return (
                <button
                  key={index}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => focusPerson(index)}
                  className={`flex min-h-[58px] items-center gap-2.5 rounded-xl border p-2.5 text-left transition-all duration-200 active:scale-98 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                    persons.length > 3 ? "w-[140px] shrink-0" : "w-full"
                  } ${
                    active
                      ? "border-transparent bg-gold-gradient text-primary-foreground shadow-gold"
                      : "border-border/45 bg-background/35 hover:border-gold-soft/30 hover:bg-background/60"
                  }`}
                >
                  <span
                    className={`flex size-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${
                      active
                        ? named
                          ? "bg-white text-amber-900 shadow-sm"
                          : "bg-white/20 text-white"
                        : named
                          ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/25"
                          : "bg-muted/70 text-muted-foreground"
                    }`}
                    aria-hidden="true"
                  >
                    {named ? <Check className="size-3.5" strokeWidth={2.5} /> : index + 1}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span
                      className={`block text-[9px] font-bold uppercase tracking-wider ${
                        active ? "text-primary-foreground/75" : "text-muted-foreground/80"
                      }`}
                    >
                      Người {index + 1}
                    </span>
                    <span
                      className={`block truncate text-xs font-semibold leading-5 ${
                        !named
                          ? active
                            ? "text-primary-foreground/60 italic font-normal"
                            : "text-muted-foreground/60 italic font-normal"
                          : active
                            ? "text-primary-foreground"
                            : "text-foreground font-semibold"
                      }`}
                    >
                      {person.birth.ho_ten.trim() || "Chưa đặt tên"}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      ) : null}

      {inputStep === "profile" ? (
        // `scroll-mt-16` chừa chỗ cho TopNav dính đỉnh — thiếu nó thì thanh nav đè
        // lên đúng hàng tab người mà `focusPerson` vừa kéo tới.
        <section
          ref={personFormRef}
          className="scroll-mt-16 divide-y divide-border/20 overflow-hidden rounded-2xl border border-gold-soft/40 bg-card/50 shadow-[0_12px_30px_rgba(77,48,19,0.05)]"
        >
          {/* Khối 1: Bạn đang nhập cho ai (chỉ hiện khi có nhiều hơn 1 người) */}
          {persons.length > 1 ? (
            <div className="space-y-3 p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 text-primary">
                  <UserRound className="size-3.5 text-primary/80" />
                  <p className="text-[11px] font-bold uppercase tracking-[0.1em]">
                    Bạn đang nhập cho ai
                  </p>
                </div>
                <p className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">
                  {namedPersonsCount}/{persons.length} người đã có tên
                </p>
              </div>
              <div
                className={`gap-2 ${
                  persons.length <= 3
                    ? `grid ${persons.length === 2 ? "grid-cols-2" : "grid-cols-3"}`
                    : "flex overflow-x-auto pb-1"
                }`}
                role="tablist"
                aria-label="Người đang xem"
              >
                {persons.map((person, index) => {
                  const named = isBirthValueComplete(person.birth);
                  const active = activePersonIndex === index;
                  // Người 1 là chủ lượt xem, không bỏ được. Từ người 2 trở đi thì nút
                  // bỏ phải nằm NGAY TRÊN THẺ: trước đó nó chỉ có trong card hồ sơ của
                  // đúng người đang mở, nên muốn bỏ người 2 phải đoán ra rằng mình cần
                  // bấm sang thẻ người 2 trước — không ai tìm ra.
                  const removable = index > 0;
                  return (
                    <div
                      key={index}
                      className={`relative ${persons.length > 3 ? "w-[140px] shrink-0" : "w-full"}`}
                    >
                      <button
                        type="button"
                        role="tab"
                        aria-selected={active}
                        onClick={() => focusPerson(index)}
                        className={`flex min-h-[58px] w-full items-center gap-2.5 rounded-xl border p-2.5 text-left transition-all duration-200 active:scale-98 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                          removable ? "pr-8" : ""
                        } ${
                          active
                            ? "border-transparent bg-gold-gradient text-primary-foreground shadow-gold"
                            : "border-border/45 bg-background/35 hover:border-gold-soft/30 hover:bg-background/60"
                        }`}
                      >
                        <span
                          className={`flex size-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${
                            active
                              ? named
                                ? "bg-white text-amber-900 shadow-sm"
                                : "bg-white/20 text-white"
                              : named
                                ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/25"
                                : "bg-muted/70 text-muted-foreground"
                          }`}
                          aria-hidden="true"
                        >
                          {named ? <Check className="size-3.5" strokeWidth={2.5} /> : index + 1}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span
                            className={`block text-[9px] font-bold uppercase tracking-wider ${
                              active ? "text-primary-foreground/75" : "text-muted-foreground/80"
                            }`}
                          >
                            Người {index + 1}
                          </span>
                          <span
                            className={`block truncate text-xs font-semibold leading-5 ${
                              !named
                                ? active
                                  ? "text-primary-foreground/60 italic font-normal"
                                  : "text-muted-foreground/60 italic font-normal"
                                : active
                                  ? "text-primary-foreground"
                                  : "text-foreground font-semibold"
                            }`}
                          >
                            {person.birth.ho_ten.trim() || "Chưa đặt tên"}
                          </span>
                        </span>
                      </button>
                      {/* Nút bỏ nằm NGOÀI thẻ: <button> lồng <button> là HTML không hợp
                        lệ, trình duyệt tự gỡ và cái bên trong mất luôn click. */}
                      {removable ? (
                        <button
                          type="button"
                          onClick={() => removePerson(index)}
                          aria-label={`Bỏ người ${index + 1}${
                            person.birth.ho_ten.trim() ? ` — ${person.birth.ho_ten.trim()}` : ""
                          }`}
                          className={`absolute right-1 top-1 grid size-6 place-items-center rounded-full transition ${
                            active
                              ? "text-primary-foreground/70 hover:bg-black/15 hover:text-primary-foreground"
                              : "text-muted-foreground/70 hover:bg-destructive/10 hover:text-destructive"
                          }`}
                        >
                          <X className="size-3.5" />
                        </button>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}

          {/* Khối 3: Thông tin hồ sơ */}
          <div className="p-4">
            {activePerson && isBirthValueComplete(activePerson.birth) && !editingProfile ? (
              <ProfileSummary
                value={activePerson.birth}
                onEdit={() => setEditingProfile(true)}
                onRemove={activePersonIndex > 0 ? () => removePerson(activePersonIndex) : undefined}
                hideBirth={!askBirthUpfront}
                embedded={true}
              />
            ) : activePerson ? (
              <BirthInputForm
                title={
                  activePersonIndex === 0 ? "Thông tin của bạn" : `Người ${activePersonIndex + 1}`
                }
                value={activePerson.birth}
                onChange={(birth) => updateActivePerson({ birth, birthTouched: true })}
                onRemove={activePersonIndex > 0 ? () => removePerson(activePersonIndex) : undefined}
                hideBirthFields={!askBirthUpfront || (birthOptional && activePerson.skipBirth)}
                showGender
                embedded={true}
                footer={
                  askBirthUpfront && birthOptional ? (
                    <label className="flex cursor-pointer items-start gap-2.5 rounded-xl border border-border/40 bg-background/30 p-2.5">
                      <input
                        type="checkbox"
                        checked={activePerson.skipBirth}
                        onChange={(e) => updateActivePerson({ skipBirth: e.target.checked })}
                        className="mt-0.5 size-4 shrink-0 accent-[#E5B869]"
                      />
                      <span className="text-xs leading-5 text-muted-foreground">
                        <span className="font-medium text-foreground">
                          Không khai ngày sinh — chỉ xem tay
                        </span>
                        <br />
                        {activePerson.skipBirth
                          ? "Thầy luận thuần theo bàn tay, không có phần đối chiếu Tử Vi – Bát Tự."
                          : birthNote}
                      </span>
                    </label>
                  ) : !askBirthUpfront ? (
                    <p className="rounded-xl border border-border/40 bg-background/30 p-2.5 text-xs leading-5 text-muted-foreground">
                      Ngày sinh sẽ được hỏi sau, khi bàn tay đã đọc xong — và chỉ khi bạn muốn thêm
                      phần đối chiếu Tử Vi – Bát Tự.
                    </p>
                  ) : null
                }
              />
            ) : null}
          </div>

          {/* Khối 4: Chọn tay để chụp */}
          {topic?.hand_choice && activePerson ? (
            <div className="p-4">
              <HandChoice
                value={activePerson.hand}
                onChange={(hand) => updateActivePerson({ hand, thumb: hand })}
                hands={topic.hands}
                recommendation={topic.hand_recommendation}
                embedded={true}
              />
            </div>
          ) : null}

          {/* Khối nút Hành động */}
          <div className="p-4 space-y-3 bg-muted/10">
            {/* Một nút, hai đích — và đích nào cũng là việc kế tiếp thật sự của thẻ.
                Người vừa khai xong mà còn người sau chưa khai thì nút đưa sang người
                sau (đây là tín hiệu "nhập xong" DUY NHẤT rõ ràng: chính họ bấm), chỉ
                khi cả thẻ đủ tên nút mới mở đường sang bước chụp ảnh. */}
            <button
              type="button"
              disabled={!goesToNextPerson && !namesReady}
              onClick={() => {
                if (goesToNextPerson) {
                  focusPerson(nextPersonNeedingInfo);
                  return;
                }
                goToStep("capture");
              }}
              className="flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-gold-gradient px-4 text-sm font-semibold text-primary-foreground shadow-gold transition disabled:cursor-not-allowed disabled:opacity-45 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              {goesToNextPerson ? (
                <>
                  Tiếp tục ·{" "}
                  {persons[nextPersonNeedingInfo]?.birth.ho_ten.trim() ||
                    `Người ${nextPersonNeedingInfo + 1}`}
                </>
              ) : (
                <>
                  <ImagePlus className="size-4" aria-hidden="true" /> Bắt đầu chụp ảnh
                </>
              )}
              <ChevronRight className="size-4" aria-hidden="true" />
            </button>
            {!namesReady && !goesToNextPerson ? (
              <p className="text-center text-[11px] text-muted-foreground">
                Điền họ tên để tiếp tục
              </p>
            ) : null}
            {persons.length < maxPersons ? (
              <button
                type="button"
                onClick={addPerson}
                className="flex min-h-11 w-full items-center justify-center gap-2 rounded-xl border border-dashed border-gold-soft/55 text-xs font-medium text-muted-foreground transition hover:border-primary/50 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <Plus className="size-4" /> Xem thêm người
                {extraPrice > 0 ? ` · +${formatVnd(extraPrice)}` : ""}
              </button>
            ) : null}
          </div>
        </section>
      ) : null}

      {inputStep === "capture" && activePerson ? (
        <div ref={personFormRef as any} className="space-y-3 scroll-mt-16">
          <section className="overflow-hidden rounded-2xl border border-gold-soft/45 bg-card/55 shadow-[0_12px_30px_rgba(77,48,19,0.05)] backdrop-blur">
            <div className="flex items-center gap-3 p-4">
              <span className="min-w-0 flex-1">
                <span className="block text-[11px] font-semibold uppercase tracking-[0.15em] text-primary/80">
                  Bước 2 · Ảnh bàn tay
                </span>
                <span className="mt-1 block truncate font-display text-base font-semibold text-foreground">
                  {activePersonName}
                </span>
              </span>
              <span
                className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                  activeReadyImagesCount === activeRequiredSlots.length
                    ? "bg-emerald-500/12 text-emerald-700 dark:text-emerald-300"
                    : "bg-primary/10 text-primary"
                }`}
              >
                {activeReadyImagesCount}/{activeRequiredSlots.length} ảnh
              </span>
            </div>

            <div className="border-t border-border/25 bg-background/15 p-4">
              <PalmSavedHandPicker
                embedded
                value={activePerson.profileId}
                personName={activePersonName}
                onPick={(profile) =>
                  updateActivePerson(
                    profile
                      ? {
                          profileId: profile.profile_id,
                          birth: {
                            ...activePerson.birth,
                            ho_ten: profile.persons[0]?.ho_ten || activePerson.birth.ho_ten,
                          },
                          hand: profile.persons[0]?.hand ?? activePerson.hand,
                          thumb: profile.persons[0]?.hand ?? activePerson.hand,
                        }
                      : { profileId: null },
                  )
                }
              />
            </div>

            <div className="border-t border-border/25 p-4">
              {slotsToShootFor(activePersonIndex).length ? (
                <GuidedHandCapture
                  embedded
                  hideHeader
                  personIndex={activePersonIndex}
                  images={images}
                  onChange={setImages}
                  // Khung của CHÍNH thẻ đang chọn, trừ đi khung mượn được từ bàn tay đã
                  // lưu. Thiếu hai prop này là mọi thẻ đều bắt chụp đủ 4 ảnh, và ba tấm
                  // thừa của thẻ 1 ảnh là ảnh THÊM CÓ PHÍ.
                  slots={slotsToShootFor(activePersonIndex)}
                  optionalSlots={optionalSlotsOf(topic, requiredSlotsFor(activePersonIndex))}
                  extraImagePrice={extraImagePrice}
                />
              ) : (
                /* Mượn đủ ảnh từ bàn tay đã lưu → kết thúc ngay trong cùng thẻ. */
                <div className="flex items-start gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/[0.06] p-3">
                  <span className="grid size-8 shrink-0 place-items-center rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300">
                    <Check className="size-4" strokeWidth={2.5} aria-hidden="true" />
                  </span>
                  <div>
                    <p className="text-[12px] font-semibold text-foreground">Đã có đủ ảnh</p>
                    <p className="mt-0.5 text-[11px] leading-4 text-muted-foreground">
                      Hồ sơ {activePersonName} đã có đủ {activeRequiredSlots.length} ảnh cho chủ đề
                      này. Bạn có thể tiếp tục ngay hoặc chọn “Chụp mới” ở trên.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </section>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setInputStep("profile")}
              className="flex min-h-12 flex-1 items-center justify-center gap-1 rounded-xl border border-border/45 text-sm font-semibold text-muted-foreground transition hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <ChevronLeft className="size-4" /> Quay lại
            </button>
            <button
              type="button"
              disabled={!activePersonImagesReady}
              onClick={() => {
                const nextPerson = persons.findIndex(
                  (_person, index) =>
                    !slotsToShootFor(index).every((slot) =>
                      images.has(palmImageField(index, slot)),
                    ),
                );
                if (nextPerson >= 0) {
                  focusPerson(nextPerson);
                  return;
                }
                if (imagesReady) setInputStep("finish");
              }}
              className="flex min-h-12 flex-[1.5] items-center justify-center gap-1 rounded-xl bg-gold-gradient text-sm font-semibold text-primary-foreground shadow-gold transition disabled:cursor-not-allowed disabled:opacity-45 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              {imagesReady ? "Tiếp tục" : "Lưu ảnh & tiếp tục"} <ChevronRight className="size-4" />
            </button>
          </div>
          {!activePersonImagesReady ? (
            <p className="text-center text-[11px] text-muted-foreground">
              Chụp đủ ảnh của người này để tiếp tục
            </p>
          ) : imagesReady ? null : (
            <p className="text-center text-[11px] text-primary">Còn ảnh của người khác cần chụp</p>
          )}
        </div>
      ) : null}

      {inputStep === "finish" ? (
        <div className="space-y-3">
          <section className="rounded-2xl border border-gold-soft/45 bg-card/55 p-4 shadow-[0_12px_30px_rgba(77,48,19,0.05)]">
            <div className="flex items-center gap-3">
              <span className="grid size-10 place-items-center rounded-xl bg-primary/10 text-primary">
                <Check className="size-5" />
              </span>
              <div className="min-w-0 flex-1">
                {/* Số ảnh theo THẺ, không cứng 4: thẻ một ảnh mà báo "đã đủ 4 ảnh"
                    thì người xem đi tìm ba tấm mình chưa chụp. */}
                <h2 className="font-display text-lg font-semibold text-foreground">
                  Đã đủ {totalRequiredImages} ảnh bàn tay
                </h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  Bạn có thể xem lại từng ảnh hoặc chỉnh tùy chọn nâng cao.
                </p>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-4 gap-2">
              {PALM_SLOTS.filter(
                (slot) =>
                  (requiredSlotsFor(activePersonIndex) ?? PALM_SLOTS).includes(slot) ||
                  images.has(palmImageField(activePersonIndex, slot)),
              ).map((slot) => {
                const file = images.get(palmImageField(activePersonIndex, slot));
                const borrowedProfileId = persons[activePersonIndex]?.profileId;
                const isBorrowed =
                  !file && borrowedProfileId && borrowedSlotsFor(activePersonIndex).includes(slot);
                return (
                  <span
                    key={slot}
                    className="grid aspect-square place-items-center overflow-hidden rounded-xl border border-border/35 bg-background/30 text-[9px] text-muted-foreground"
                  >
                    {isBorrowed ? (
                      <ProfileSlotThumb profileId={borrowedProfileId} slot={slot} />
                    ) : (
                      <FileThumb file={file} />
                    )}
                  </span>
                );
              })}
            </div>
          </section>
          {/* Câu hỏi và chọn thầy đã chuyển xuống bước sau bảng (`PalmWrapUpStep`) —
              bàn tay xử lý xong đã, rồi mới tới lượt người xem hỏi. Chỉ thẻ BẮT BUỘC
              nêu câu hỏi mới hỏi ở đây, vì nó là điều kiện dùng thẻ và phải biết
              TRƯỚC khi trừ tiền. */}
          {askQuestionUpfront ? (
            <section className="space-y-3 rounded-2xl border border-gold-soft/45 bg-card/55 p-4">
              <label className="block">
                <span className="mb-1.5 block text-sm font-semibold text-foreground">
                  Thẻ này cần bạn nêu câu hỏi
                </span>
                <textarea
                  value={question}
                  onChange={(e) => setQuestion(e.target.value.slice(0, 2000))}
                  rows={3}
                  placeholder="Ví dụ: Sức khoẻ và công việc năm nay của tôi ra sao?"
                  className="input-mystic w-full resize-none"
                />
              </label>
            </section>
          ) : null}

          <button
            type="button"
            onClick={() => setAdvancedOpen(true)}
            className="group relative flex min-h-[76px] w-full items-center gap-3 overflow-hidden rounded-2xl border border-primary/55 bg-gradient-to-r from-primary/15 via-primary/5 to-card/75 px-4 py-3 text-left shadow-[0_10px_28px_rgba(154,105,32,0.14)] transition hover:border-primary/75 hover:shadow-[0_12px_32px_rgba(154,105,32,0.2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <span
              className="absolute inset-y-3 left-0 w-1 rounded-r-full bg-primary"
              aria-hidden="true"
            />
            <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-primary/15 text-primary ring-1 ring-primary/25">
              <Settings2 className="size-5" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="flex flex-wrap items-center gap-1.5">
                <span className="text-sm font-bold text-foreground">Tăng độ chi tiết</span>
                <span className="rounded-full bg-primary px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.08em] text-primary-foreground shadow-sm">
                  {advancedSelectionCount > 0
                    ? `Đã thêm ${advancedSelectionCount}/3`
                    : "Khuyên dùng"}
                </span>
              </span>
              <span className="mt-1 block text-[11px] leading-4 text-muted-foreground">
                Tay chính · Vân đầu ngón · Móng tay
              </span>
              <span className="mt-1 block text-[10px] font-semibold text-primary/85">
                Không bắt buộc · giúp thầy soi kỹ hơn
              </span>
            </span>
            <span className="grid size-8 shrink-0 place-items-center rounded-full bg-primary/12 text-primary transition group-hover:bg-primary group-hover:text-primary-foreground">
              <ChevronRight className="size-4" />
            </span>
          </button>

          {/* Card giá nằm trong luồng nội dung để không che bảng chọn trên mobile. */}
          <div className="mt-3 rounded-2xl border border-gold-soft/50 bg-card/50 p-3 md:p-4">
            <div className="mb-2 flex items-baseline justify-between text-[12px]">
              <span className="text-muted-foreground">
                {persons.length} người · {totalRequiredImages} ảnh
                {extraImagesCount > 0 ? ` + ${extraImagesCount} ảnh thêm` : ""}
              </span>
              <span className="font-semibold text-primary">{formatVnd(price)}</span>
            </div>

            <Button
              onClick={() => start.mutate()}
              disabled={!canStart || start.isPending}
              className="mt-3 h-12 w-full rounded-xl bg-gold-gradient text-primary-foreground shadow-gold"
            >
              {start.isPending ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
              {/* ⚠️ Nút này TRỪ TIỀN ngay (call quét là chỗ tốn tiền thật, xem
                  `start` ở trên). Nhãn cũ — "Dò nét chỉ tay" + "Chưa trừ tiền" —
                  là chữ còn sót lại từ thời tiền trừ ở bước luận; để nguyên là hứa
                  miễn phí rồi trừ tiền ngay lượt bấm đầu tiên. */}
              {reviewEnabled ? `Đọc bàn tay · ${formatVnd(price)}` : "Xem tướng tay"}
            </Button>
            <p className="mt-1.5 text-center text-[10px] leading-4 text-muted-foreground">
              {reviewEnabled
                ? `Trừ ${formatVnd(price)} ở bước này. Sau khi máy đọc xong, bạn được sửa và bổ sung bảng miễn phí rồi thầy mới luận.`
                : "Bài lưu lại để mở đọc miễn phí sau này."}
            </p>
          </div>
        </div>
      ) : null}

      {activePerson ? (
        <PalmAdvancedOptionsDialog
          open={advancedOpen}
          onOpenChange={setAdvancedOpen}
          person={activePerson}
          config={config.data}
          onChange={(next) => updateActivePerson(next)}
        />
      ) : null}
    </Shell>
  );
}
